// RigSentinel Calc - catalog of categories and calculator definitions
import * as C from "./calculators";

// Field definitions: { key, label, unit, step?, placeholder?, type? }
export const CATEGORIES = [
  { id: "converters", name: "Unit Converters", short: "Converters", icon: "Repeat", desc: "Length, pressure, volume, density, flow, temperature." },
  { id: "drilling", name: "Drilling Calculations", short: "Drilling", icon: "Drill", desc: "Hydrostatic, capacities, pump output, circulation." },
  { id: "wellcontrol", name: "Well Control", short: "Well Control", icon: "ShieldAlert", desc: "Kill mud weight, ICP/FCP, formation pressure." },
  { id: "trip", name: "Trip Calculations", short: "Trip", icon: "ArrowsUpFromLine", desc: "Displacement, trip tank fill, slug volume." },
  { id: "mud", name: "Mud & Fluids", short: "Mud", icon: "Beaker", desc: "Barite weight-up, dilution, volume increase." },
  { id: "hydraulics", name: "Hydraulics", short: "Hydraulics", icon: "Waves", desc: "Annular velocity, ECD, pressure drop, surge/swab, bit HHP." },
  { id: "cement", name: "Cementing", short: "Cementing", icon: "Boxes", desc: "Slurry, yield, sacks, mix water, spacer." },
  { id: "casing", name: "Casing & Tubing", short: "Casing", icon: "Container", desc: "Capacities, annular, buoyancy, shoe track." },
  { id: "directional", name: "Directional Drilling", short: "Directional", icon: "Compass", desc: "Dogleg severity, MD↔TVD, build rate, horizontal displacement." },
  { id: "workover", name: "Workover & Completion", short: "Workover", icon: "Wrench", desc: "Tubing stretch, BHP/WHP, kill fluid, perforation pressure." },
  { id: "stuck", name: "Stuck Pipe & Fishing", short: "Stuck/Fish", icon: "Magnet", desc: "Free-point estimator, pipe light/heavy, safe-pull margin." },
  { id: "tubulars", name: "Tubulars", short: "Tubulars", icon: "Layers", desc: "Burst (Barlow), axial with buoyancy, collapse/tensile placeholders." },
  { id: "production", name: "Production", short: "Production", icon: "Gauge", desc: "Water cut, GOR, BPD, IPR/Vogel, PI, ESP sizing, nodal." },
  { id: "subsea-rig-formulas", name: "Subsea Rig Formulas", short: "Subsea Rig", icon: "Anchor", desc: "Offshore / subsea-specific calculations for floating rigs, drillships, semi-subs, and subsea BOP operations.", stackMode: "subsea" },
  { id: "library", name: "Field Data Library", short: "Library", icon: "BookOpen", desc: "Constants, glossaries, safety notes." },
];

export function getCategory(id) { return CATEGORIES.find((c) => c.id === id); }

// Calculator definitions
export const CALCULATORS = [
  // DRILLING
  {
    id: "hydrostatic_pressure", category: "drilling", name: "Hydrostatic Pressure",
    description: "Calculates the vertical column pressure of fluid at a given true vertical depth.",
    fields: [
      // RC-1 Reusable RCE Consumer Framework — POC calculator wiring.
      // `rcePath` is the optional binding the framework reads on mount.
      // No math touched; only the seed value changes for users with an
      // active Engineering Session.
      { key: "mw",  label: "Mud Weight",          unit: "ppg", placeholder: "e.g. 9.6",   rcePath: "mud.weightInPpg" },
      { key: "tvd", label: "True Vertical Depth", unit: "ft",  placeholder: "e.g. 10000", rcePath: "well.currentTvdFt" },
    ],
    fn: C.hydrostaticPressure,
    to: [{ id: "ecd", fieldKey: "annPressureLoss", note: "as ΔP_ann input" }],
  },
  {
    id: "pressure_gradient", category: "drilling", name: "Pressure Gradient",
    description: "Converts mud weight (ppg) into pressure gradient (psi/ft).",
    fields: [{ key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 9.6" }],
    fn: C.pressureGradient,
  },
  {
    id: "hole_volume", category: "drilling", name: "Hole Volume",
    description: "Open-hole capacity volume for a given diameter and length.",
    fields: [
      { key: "diameter", label: "Hole Diameter", unit: "in", placeholder: "e.g. 8.5" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 1500" },
    ],
    fn: C.holeVolume,
  },
  {
    id: "pipe_capacity", category: "drilling", name: "Single Tubular Capacity",
    description: "Internal capacity of a single tubular section based on its ID and length. For a full string made of multiple components, use Total Drill String Internal Volume.",
    fields: [
      { key: "id", label: "Pipe ID", unit: "in", placeholder: "e.g. 4.276" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 9500" },
    ],
    fn: C.pipeCapacity,
  },
  {
    id: "annular_capacity", category: "drilling", name: "Annular Capacity",
    description: "Open or cased-hole annular volume between hole/casing ID and pipe OD.",
    fields: [
      { key: "dh", label: "Hole / Casing ID", unit: "in", placeholder: "e.g. 8.5", libraryKind: ["casing", "bit"] },
      { key: "dp", label: "Pipe OD", unit: "in", placeholder: "e.g. 5", libraryKind: ["drill_pipe", "hwdp", "drill_collar", "tubing"] },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.annularCapacity,
  },
  {
    id: "pump_output", category: "drilling", name: "Pump Output",
    description: "Theoretical output per stroke for triplex or duplex mud pumps.",
    fields: [
      { key: "pumpType", label: "Pump Type", unit: "", type: "select", options: ["triplex", "duplex"], placeholder: "triplex" },
      { key: "linerId", label: "Liner ID", unit: "in", placeholder: "e.g. 6" },
      { key: "stroke", label: "Stroke Length", unit: "in", placeholder: "e.g. 12" },
      { key: "rodOd", label: "Rod OD (duplex only)", unit: "in", placeholder: "e.g. 2", optional: true },
      { key: "efficiency", label: "Efficiency (0-1)", unit: "", placeholder: "0.95" },
    ],
    fn: C.pumpOutput,
    to: [
      { id: "strokes_to_bit", fieldKey: "pumpBblStk" },
      { id: "bottoms_up_strokes", fieldKey: "pumpBblStk" },
    ],
  },
  {
    id: "strokes_to_bit", category: "drilling", name: "Strokes to Bit from Drill String Volume",
    description: "Strokes required to displace the total drill-string internal volume to the bit. Pair with Total Drill String Internal Volume.",
    fields: [
      { key: "pipeVolume", label: "Total Drill String Internal Volume", unit: "bbl", placeholder: "e.g. 120" },
      { key: "pumpBblStk", label: "Pump Output", unit: "bbl/stk", placeholder: "e.g. 0.105", libraryKind: ["pump"] },
    ],
    fn: C.strokesToBit,
  },
  {
    id: "bottoms_up_strokes", category: "drilling", name: "Bottoms-Up Strokes",
    description: "Strokes to circulate annular contents from bottom to surface. Pair with Multi-Section Annular Volume for accuracy.",
    fields: [
      { key: "annularVolume", label: "Total Annular Volume", unit: "bbl", placeholder: "e.g. 350" },
      { key: "pumpBblStk", label: "Pump Output", unit: "bbl/stk", placeholder: "e.g. 0.105" },
    ],
    fn: C.bottomsUpStrokes,
  },
  {
    id: "circulation_time", category: "drilling", name: "Circulation Time",
    description: "Time to circulate a given volume at a given flow rate.",
    fields: [
      { key: "totalVolume", label: "Total Volume", unit: "bbl", placeholder: "e.g. 500" },
      { key: "flowRate", label: "Flow Rate", unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.circulationTime,
  },

  // TON-MILE SUPPORT CALCULATORS (F2a) — drilling-line documentation aids.
  {
    id: "round_trip_ton_miles", category: "drilling", name: "Round Trip Ton Miles (Support Estimate)",
    description: "Simplified support estimate of drilling-line ton-mile accumulation for a round trip. Documentation support only — does not replace contractor slip-and-cut programs or company procedures.",
    fields: [
      { key: "hookload", label: "Hookload / String Weight", unit: "lbf", placeholder: "e.g. 250000" },
      { key: "tripDepth", label: "Trip Depth", unit: "ft", placeholder: "e.g. 10000" },
      { key: "blockWeightAllowance", label: "Block Weight Allowance", unit: "lbf", placeholder: "e.g. 25000", optional: true },
      { key: "linesStrung", label: "Number of Lines Strung (documentation only)", unit: "", placeholder: "e.g. 10", optional: true },
      { key: "correctionFactor", label: "Efficiency / Correction Factor", unit: "", placeholder: "default 1.0", optional: true },
      { key: "operationNote", label: "Operation Note / Procedure Reference", unit: "", type: "text", placeholder: "e.g. Round trip after BHA change", optional: true },
    ],
    fn: C.roundTripTonMiles,
  },
  {
    id: "single_trip_ton_miles", category: "drilling", name: "Single Trip / One-Way Ton Miles (Support Estimate)",
    description: "Simplified support estimate of drilling-line ton-mile accumulation for one-way movement. Documentation support only.",
    fields: [
      { key: "hookload", label: "Hookload / String Weight", unit: "lbf", placeholder: "e.g. 250000" },
      { key: "movementDistance", label: "Movement Distance", unit: "ft", placeholder: "e.g. 10000" },
      { key: "blockWeightAllowance", label: "Block Weight Allowance", unit: "lbf", placeholder: "e.g. 25000", optional: true },
      { key: "linesStrung", label: "Number of Lines Strung (documentation only)", unit: "", placeholder: "e.g. 10", optional: true },
      { key: "correctionFactor", label: "Efficiency / Correction Factor", unit: "", placeholder: "default 1.0", optional: true },
    ],
    fn: C.singleTripTonMiles,
  },
  {
    id: "cumulative_ton_miles", category: "drilling", name: "Cumulative Ton Miles Since Slip-and-Cut",
    description: "Tracks cumulative ton-mile accumulation from multiple operations and compares against an entered planning threshold. Documentation support only.",
    fields: [
      { key: "currentCumulative", label: "Current Accumulated Ton-Miles", unit: "ton-mi", placeholder: "e.g. 320.0", optional: true },
      { key: "newOperationTM", label: "New Operation Ton-Miles", unit: "ton-mi", placeholder: "e.g. 45.0", optional: true },
      { key: "slipAndCutThreshold", label: "Slip-and-Cut Threshold / Planned Interval", unit: "ton-mi", placeholder: "e.g. 1000", optional: true },
      { key: "notes", label: "Notes / Procedure Reference", unit: "", type: "text", placeholder: "e.g. After trip out of hole", optional: true },
    ],
    fn: C.cumulativeTonMiles,
  },

  // WELL CONTROL
  {
    id: "kill_mud_weight", category: "wellcontrol", name: "Kill Mud Weight",
    description: "Required new mud weight to balance formation pressure after a kick.",
    wellControl: true,
    fields: [
      { key: "sidpp", label: "SIDPP", unit: "psi", placeholder: "e.g. 250" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 12000" },
      { key: "currentMW", label: "Current MW", unit: "ppg", placeholder: "e.g. 9.6", rcePath: "mud.weightInPpg" },
    ],
    fn: C.killMudWeight,
  },
  {
    id: "icp", category: "wellcontrol", name: "Initial Circulating Pressure (ICP)",
    description: "Initial pressure to circulate out a kick.",
    wellControl: true,
    fields: [
      { key: "sidpp", label: "SIDPP", unit: "psi", placeholder: "e.g. 250" },
      { key: "scr", label: "Slow Circulation Rate Pressure (SCR)", unit: "psi", placeholder: "e.g. 600" },
    ],
    fn: C.initialCirculatingPressure,
  },
  {
    id: "fcp", category: "wellcontrol", name: "Final Circulating Pressure (FCP)",
    description: "Pressure to hold once kill mud has reached the bit.",
    wellControl: true,
    fields: [
      { key: "scr", label: "SCR Pressure", unit: "psi", placeholder: "e.g. 600" },
      { key: "kmw", label: "Kill Mud Weight (KMW)", unit: "ppg", placeholder: "e.g. 10.6" },
      { key: "omw", label: "Original Mud Weight (OMW)", unit: "ppg", placeholder: "e.g. 9.6" },
    ],
    fn: C.finalCirculatingPressure,
  },
  {
    id: "formation_pressure", category: "wellcontrol", name: "Formation Pressure from SIDPP",
    description: "Bottom-hole formation pressure from SIDPP and mud column hydrostatic.",
    wellControl: true,
    fields: [
      { key: "sidpp", label: "SIDPP", unit: "psi", placeholder: "e.g. 250" },
      { key: "omw", label: "Original Mud Weight", unit: "ppg", placeholder: "e.g. 9.6" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 12000" },
    ],
    fn: C.formationPressureFromSIDPP,
  },
  {
    id: "shut_in_sheet", category: "wellcontrol", name: "Shut-In Data Sheet",
    description: "Combined kick assessment: KMW, formation pressure, status from shut-in data.",
    wellControl: true,
    fields: [
      { key: "sidpp", label: "SIDPP", unit: "psi", placeholder: "e.g. 250" },
      { key: "sicp", label: "SICP", unit: "psi", placeholder: "e.g. 320" },
      { key: "pitGain", label: "Pit Gain", unit: "bbl", placeholder: "e.g. 12" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 12000" },
      { key: "omw", label: "OMW", unit: "ppg", placeholder: "e.g. 9.6" },
    ],
    fn: C.shutInDataSheet,
  },

  // TRIP
  {
    id: "pipe_displacement", category: "trip", name: "Pipe Displacement",
    description: "Steel displacement volume of pipe pulled or run.",
    fields: [
      { key: "od", label: "Pipe OD", unit: "in", placeholder: "e.g. 5" },
      { key: "id", label: "Pipe ID", unit: "in", placeholder: "e.g. 4.276" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 93" },
    ],
    fn: C.pipeDisplacement,
  },
  {
    id: "trip_tank_fill", category: "trip", name: "Expected Trip Tank Fill",
    description: "Mud volume expected to fill the hole based on displacement.",
    fields: [
      { key: "displacementBblFt", label: "Displacement", unit: "bbl/ft", placeholder: "e.g. 0.00755" },
      { key: "lengthPulled", label: "Length Pulled", unit: "ft", placeholder: "e.g. 93" },
    ],
    fn: C.expectedTripTankFill,
  },
  {
    id: "pulled_vs_filled", category: "trip", name: "Volume Pulled vs Volume Filled",
    description: "Compares expected and actual trip tank fill to flag gain or loss.",
    fields: [
      { key: "expectedFill", label: "Expected Fill", unit: "bbl", placeholder: "e.g. 0.7" },
      { key: "actualFill", label: "Actual Fill", unit: "bbl", placeholder: "e.g. 0.95" },
    ],
    fn: C.pulledVsFilled,
  },
  {
    id: "slug_volume", category: "trip", name: "Slug Volume",
    description: "Volume of heavy slug to give a dry pipe length on trips.",
    fields: [
      { key: "dpCapacity", label: "DP Capacity", unit: "bbl/ft", placeholder: "e.g. 0.01776" },
      { key: "dryPipeLength", label: "Desired Dry Pipe Length", unit: "ft", placeholder: "e.g. 100" },
      { key: "slugWeight", label: "Slug Weight", unit: "ppg", placeholder: "e.g. 13.0" },
      { key: "mudWeight", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 9.6" },
    ],
    fn: C.slugVolume,
  },

  // MUD
  {
    id: "barite_required", category: "mud", name: "Barite Required",
    description: "Sacks of barite to weight up the active system.",
    fields: [
      { key: "currentMW", label: "Current MW", unit: "ppg", placeholder: "e.g. 9.6" },
      { key: "desiredMW", label: "Desired MW", unit: "ppg", placeholder: "e.g. 10.6" },
      { key: "volumeBbl", label: "System Volume", unit: "bbl", placeholder: "e.g. 800" },
    ],
    fn: C.bariteRequired,
  },
  {
    id: "dilution_volume", category: "mud", name: "Dilution Volume",
    description: "Volume of dilution fluid to reduce mud weight.",
    fields: [
      { key: "currentMW", label: "Current MW", unit: "ppg", placeholder: "e.g. 10.6" },
      { key: "desiredMW", label: "Desired MW", unit: "ppg", placeholder: "e.g. 9.6" },
      { key: "dilutionFluidMW", label: "Dilution Fluid MW", unit: "ppg", placeholder: "e.g. 8.33" },
      { key: "volumeBbl", label: "Current Volume", unit: "bbl", placeholder: "e.g. 500" },
    ],
    fn: C.dilutionVolume,
  },
  {
    id: "mw_increase", category: "mud", name: "Mud Weight Increase",
    description: "Barite required for a target weight up (alias of Barite Required).",
    fields: [
      { key: "currentMW", label: "Current MW", unit: "ppg", placeholder: "9.6" },
      { key: "desiredMW", label: "Desired MW", unit: "ppg", placeholder: "10.6" },
      { key: "volumeBbl", label: "Volume", unit: "bbl", placeholder: "800" },
    ],
    fn: C.mudWeightIncrease,
  },
  {
    id: "mw_reduction", category: "mud", name: "Mud Weight Reduction",
    description: "Water (or fluid) volume to dilute mud weight down.",
    fields: [
      { key: "currentMW", label: "Current MW", unit: "ppg", placeholder: "10.6" },
      { key: "desiredMW", label: "Desired MW", unit: "ppg", placeholder: "9.6" },
      { key: "dilutionFluidMW", label: "Dilution Fluid MW", unit: "ppg", placeholder: "8.33" },
      { key: "volumeBbl", label: "Current Volume", unit: "bbl", placeholder: "500" },
    ],
    fn: C.mudWeightReduction,
  },
  {
    id: "vol_increase_weightup", category: "mud", name: "Volume Increase After Weight-Up",
    description: "Resulting volume increase after adding barite.",
    fields: [
      { key: "currentMW", label: "Current MW", unit: "ppg", placeholder: "9.6" },
      { key: "desiredMW", label: "Desired MW", unit: "ppg", placeholder: "10.6" },
      { key: "volumeBbl", label: "System Volume", unit: "bbl", placeholder: "800" },
    ],
    fn: C.volumeIncreaseWeightUp,
  },

  // HYDRAULICS
  {
    id: "annular_velocity", category: "hydraulics", name: "Annular Velocity",
    description: "Annular velocity (ft/min) for a given flow and geometry.",
    fields: [
      { key: "flowRate", label: "Flow Rate", unit: "gpm", placeholder: "e.g. 400" },
      { key: "dh", label: "Hole / Casing ID", unit: "in", placeholder: "e.g. 8.5" },
      { key: "dp", label: "Pipe OD", unit: "in", placeholder: "e.g. 5" },
    ],
    fn: C.annularVelocity,
  },

  // CEMENT
  {
    id: "cement_slurry_volume", category: "cement", name: "Cement Slurry Volume",
    description: "Required slurry volume to fill the annulus with excess.",
    fields: [
      { key: "casingOd", label: "Casing OD", unit: "in", placeholder: "e.g. 7" },
      { key: "holeId", label: "Hole ID", unit: "in", placeholder: "e.g. 8.5" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 2000" },
      { key: "excess", label: "Excess", unit: "%", placeholder: "e.g. 30" },
    ],
    fn: C.cementSlurryVolume,
  },
  {
    id: "sacks_required", category: "cement", name: "Sacks Required",
    description: "Sacks of cement for a slurry volume given a known yield.",
    fields: [
      { key: "slurryVolumeBbl", label: "Slurry Volume", unit: "bbl", placeholder: "e.g. 150" },
      { key: "yieldFt3Sx", label: "Yield", unit: "ft³/sx", placeholder: "e.g. 1.18" },
    ],
    fn: C.sacksRequired,
  },
  // BALANCED PLUG SUPPORT CALCULATOR (F2b) — 4 modes via dropdown.
  {
    id: "balanced_plug_support", category: "cement", name: "Cement / Mud Balanced Plug Support Calculator",
    description: "Four-mode support calculator for balanced plug planning. Mode A: required slurry volume from desired plug length. Mode B: plug length from slurry volume. Mode C: workstring displacement volume (with optional strokes/time). Mode D: hydrostatic balance check across entered fluid columns. Documentation support only.",
    fields: [
      { key: "mode", label: "Mode", unit: "", type: "select", options: [
        "Mode A — Plug Volume From Desired Length",
        "Mode B — Plug Length From Slurry Volume",
        "Mode C — Workstring Displacement Volume",
        "Mode D — Hydrostatic Balance Check",
      ] },
      // Mode A + B shared
      { key: "holeId", label: "Hole / Casing Inside Diameter (Modes A & B)", unit: "in", placeholder: "e.g. 8.535", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode A", "Mode B"] }, libraryKind: ["casing", "bit"] },
      { key: "workstringOd", label: "Workstring Outside Diameter (Modes A & B — optional)", unit: "in", placeholder: "e.g. 5.0", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode A", "Mode B"] }, libraryKind: ["drill_pipe", "hwdp", "drill_collar", "tubing"] },
      { key: "excessPct", label: "Excess Percentage (Modes A & B — defaults to 0)", unit: "%", placeholder: "e.g. 25", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode A", "Mode B"] } },
      // Mode A only
      { key: "desiredPlugLength", label: "Desired Plug Length (Mode A only)", unit: "ft", placeholder: "e.g. 300", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode A"] } },
      { key: "slurryYieldFt3PerSk", label: "Slurry Yield (Mode A — optional, ft³/sk)", unit: "ft³/sk", placeholder: "e.g. 1.18", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode A"] } },
      // Mode B only
      { key: "slurryVolumeBbl", label: "Slurry Volume (Mode B only)", unit: "bbl", placeholder: "e.g. 50", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode B"] } },
      // Mode C only
      { key: "workstringId", label: "Workstring Inside Diameter (Mode C only)", unit: "in", placeholder: "e.g. 4.276", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode C"] }, libraryKind: ["drill_pipe", "hwdp", "drill_collar", "tubing"] },
      { key: "workstringLengthFt", label: "Workstring Length to Plug-Placement Depth (Mode C only)", unit: "ft", placeholder: "e.g. 8500", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode C"] } },
      { key: "pumpOutputBblPerStk", label: "Pump Output (Mode C — optional, bbl/stk)", unit: "bbl/stk", placeholder: "e.g. 0.117", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode C"] }, libraryKind: ["pump"] },
      { key: "pumpOutputBblPerMin", label: "Pump Output (Mode C — optional, bbl/min)", unit: "bbl/min", placeholder: "e.g. 6.0", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode C"] } },
      // Mode D only
      { key: "mudWeight", label: "Mud Weight (Mode D only)", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 9.6", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode D"] } },
      { key: "mudColumnLength", label: "Mud Column Length (Mode D only)", unit: "ft", placeholder: "e.g. 8200", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode D"] } },
      { key: "slurryWeight", label: "Slurry Weight (Mode D only)", unit: "ppg", placeholder: "e.g. 15.8", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode D"] } },
      { key: "slurryColumnLength", label: "Slurry Column Length (Mode D only)", unit: "ft", placeholder: "e.g. 300", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode D"] } },
      { key: "spacerWeight", label: "Spacer Weight (Mode D — optional, must enter both spacer fields together)", unit: "ppg", placeholder: "e.g. 12.0", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode D"] } },
      { key: "spacerColumnLength", label: "Spacer Column Length (Mode D — optional, must enter both spacer fields together)", unit: "ft", placeholder: "e.g. 100", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode D"] } },
      { key: "tvdReference", label: "TVD Reference (Mode D — optional, documentation only)", unit: "ft", placeholder: "e.g. 8500", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode D"] } },
    ],
    fn: C.balancedPlug,
  },
  {
    id: "cement_yield", category: "cement", name: "Cement Yield",
    description: "Slurry yield from cement + additives mass and slurry density.",
    fields: [
      { key: "cementWeight", label: "Cement", unit: "lb/sx", placeholder: "94" },
      { key: "additivesWeight", label: "Additives", unit: "lb/sx", placeholder: "0", optional: true },
      { key: "slurryDensity", label: "Slurry Density", unit: "ppg", placeholder: "15.6" },
    ],
    fn: C.cementYield,
  },
  {
    id: "mix_water", category: "cement", name: "Mix Water Required",
    description: "Total mix water for a job at a given mix-water ratio.",
    fields: [
      { key: "sacks", label: "Sacks", unit: "sx", placeholder: "e.g. 715" },
      { key: "gallonsPerSack", label: "Mix Water", unit: "gal/sx", placeholder: "e.g. 5.2" },
    ],
    fn: C.mixWaterRequired,
  },
  {
    id: "spacer_volume", category: "cement", name: "Spacer Volume",
    description: "Spacer volume to cover a given annular length.",
    fields: [
      { key: "holeId", label: "Hole ID", unit: "in", placeholder: "8.5" },
      { key: "casingOd", label: "Casing OD", unit: "in", placeholder: "7" },
      { key: "lengthCovered", label: "Length Covered", unit: "ft", placeholder: "500" },
    ],
    fn: C.spacerVolume,
  },
  {
    id: "displacement_volume_cement", category: "cement", name: "Displacement Volume",
    description: "Volume required to displace cement to the float / plug.",
    fields: [
      { key: "casingId", label: "Casing ID", unit: "in", placeholder: "6.276" },
      { key: "plugDepth", label: "Plug Depth", unit: "ft", placeholder: "e.g. 9500" },
    ],
    fn: C.displacementVolumeCement,
  },

  // CASING & TUBING
  {
    id: "casing_capacity", category: "casing", name: "Casing Capacity",
    description: "Internal capacity volume of casing string.",
    fields: [
      { key: "id", label: "Casing ID", unit: "in", placeholder: "e.g. 6.276", libraryKind: ["casing"] },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 9500" },
    ],
    fn: C.casingCapacity,
  },
  {
    id: "tubing_capacity", category: "casing", name: "Tubing Capacity",
    description: "Internal capacity volume of tubing.",
    fields: [
      { key: "id", label: "Tubing ID", unit: "in", placeholder: "e.g. 2.441", libraryKind: ["tubing"] },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 8000" },
    ],
    fn: C.tubingCapacity,
  },
  {
    id: "annular_volume_casing", category: "casing", name: "Annular Volume",
    description: "Annular volume between two strings or hole and string.",
    fields: [
      { key: "dh", label: "Outer ID", unit: "in", placeholder: "e.g. 8.5", libraryKind: ["casing", "bit"] },
      { key: "dp", label: "Inner OD", unit: "in", placeholder: "e.g. 5", libraryKind: ["drill_pipe", "hwdp", "drill_collar", "tubing"] },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.annularVolume,
  },
  {
    id: "buoyancy_factor", category: "casing", name: "Buoyancy Factor",
    description: "Buoyancy factor for steel in mud.",
    fields: [{ key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 9.6" }],
    fn: C.buoyancyFactor,
  },
  {
    id: "shoe_track_volume", category: "casing", name: "Shoe Track Volume",
    description: "Volume between float collar and float shoe.",
    fields: [
      { key: "casingId", label: "Casing ID", unit: "in", placeholder: "e.g. 6.276" },
      { key: "shoeTrackLength", label: "Shoe Track Length", unit: "ft", placeholder: "e.g. 80" },
    ],
    fn: C.shoeTrackVolume,
  },

  // PRODUCTION
  {
    id: "water_cut", category: "production", name: "Water Cut",
    description: "Percentage of water in produced liquid stream.",
    fields: [
      { key: "waterBpd", label: "Water Rate", unit: "bbl/d", placeholder: "e.g. 300" },
      { key: "oilBpd", label: "Oil Rate", unit: "bbl/d", placeholder: "e.g. 700" },
    ],
    fn: C.waterCut,
  },
  {
    id: "gor", category: "production", name: "Gas-Oil Ratio (GOR)",
    description: "Gas volume per barrel of oil produced.",
    fields: [
      { key: "gasScf", label: "Gas Volume", unit: "scf", placeholder: "e.g. 350000" },
      { key: "oilBbl", label: "Oil Volume", unit: "bbl", placeholder: "e.g. 500" },
    ],
    fn: C.gasOilRatio,
  },
  {
    id: "bpd", category: "production", name: "Barrels Per Day",
    description: "Daily rate based on barrels produced over a given period.",
    fields: [
      { key: "barrels", label: "Barrels Produced", unit: "bbl", placeholder: "e.g. 240" },
      { key: "hours", label: "Hours", unit: "hr", placeholder: "e.g. 12" },
    ],
    fn: C.barrelsPerDay,
  },
  {
    id: "downtime_loss", category: "production", name: "Downtime Production Loss",
    description: "Deferred production due to downtime.",
    fields: [
      { key: "bpd", label: "Production Rate", unit: "BPD", placeholder: "e.g. 1200" },
      { key: "downtimeHours", label: "Downtime", unit: "hr", placeholder: "e.g. 8" },
    ],
    fn: C.downtimeProductionLoss,
  },
  {
    id: "vogel_ipr", category: "production", name: "Vogel IPR — Saturated Reservoir",
    description: "Solution-gas drive IPR: estimate flow at any Pwf using a test point.",
    fields: [
      { key: "reservoirPressure", label: "Reservoir Pressure (Pr)", unit: "psi", placeholder: "e.g. 2500" },
      { key: "testFlowRate", label: "Test Flow Rate", unit: "BPD", placeholder: "e.g. 600" },
      { key: "testPwf", label: "Test Flowing BHP (Pwf_test)", unit: "psi", placeholder: "e.g. 1800" },
      { key: "targetPwf", label: "Target Flowing BHP (Pwf)", unit: "psi", placeholder: "e.g. 1500" },
    ],
    fn: C.vogelIPR,
  },
  {
    id: "productivity_index", category: "production", name: "Productivity Index (J)",
    description: "Straight-line IPR for single-phase (undersaturated) flow.",
    fields: [
      { key: "flowRate", label: "Flow Rate (q)", unit: "BPD", placeholder: "e.g. 850" },
      { key: "reservoirPressure", label: "Reservoir Pressure (Pr)", unit: "psi", placeholder: "e.g. 3200" },
      { key: "flowingPressure", label: "Flowing BHP (Pwf)", unit: "psi", placeholder: "e.g. 2400" },
    ],
    fn: C.productivityIndex,
  },
  {
    id: "esp_tdh", category: "production", name: "ESP Total Dynamic Head",
    description: "Total dynamic head an ESP must overcome: lift + friction + wellhead head.",
    fields: [
      { key: "workingFluidLevel", label: "Working Fluid Level (WFL)", unit: "ft", placeholder: "e.g. 4500" },
      { key: "frictionLoss", label: "Friction Loss", unit: "ft", placeholder: "e.g. 120" },
      { key: "wellheadPressure", label: "Wellhead Pressure", unit: "psi", placeholder: "e.g. 150" },
      { key: "fluidSG", label: "Fluid Specific Gravity", unit: "", placeholder: "e.g. 0.96" },
    ],
    fn: C.espTDH,
  },
  {
    id: "esp_hp", category: "production", name: "ESP Brake Horsepower",
    description: "Hydraulic and brake horsepower required at TDH and rate.",
    fields: [
      { key: "flowRate", label: "Flow Rate (q)", unit: "BPD", placeholder: "e.g. 1500" },
      { key: "tdh", label: "Total Dynamic Head (TDH)", unit: "ft", placeholder: "e.g. 5200" },
      { key: "fluidSG", label: "Fluid SG", unit: "", placeholder: "e.g. 0.96" },
      { key: "efficiency", label: "Pump Efficiency (0–1)", unit: "", placeholder: "e.g. 0.65" },
    ],
    fn: C.espHydraulicHP,
  },
  {
    id: "nodal_deliverability", category: "production", name: "Nodal Deliverability (Linear IPR)",
    description: "Flow rate at a given intake pressure using straight-line IPR (J · drawdown).",
    fields: [
      { key: "reservoirPressure", label: "Reservoir Pressure (Pr)", unit: "psi", placeholder: "e.g. 3200" },
      { key: "productivityIndex", label: "Productivity Index (J)", unit: "BPD/psi", placeholder: "e.g. 1.06" },
      { key: "targetPwf", label: "Target Intake / Pwf", unit: "psi", placeholder: "e.g. 1800" },
    ],
    fn: C.nodalDeliverability,
  },

  // HYDRAULICS — expanded
  {
    id: "ecd", category: "hydraulics", name: "Equivalent Circulating Density (ECD)",
    description: "Effective downhole density while circulating, accounting for annular pressure loss.",
    fields: [
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 9.6" },
      { key: "annPressureLoss", label: "Annular Pressure Loss", unit: "psi", placeholder: "e.g. 220" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
    ],
    fn: C.ecd,
  },
  {
    id: "annular_pressure_loss", category: "hydraulics", name: "Annular Pressure Drop",
    description: "Bingham-plastic estimate of annular frictional pressure loss.",
    fields: [
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "9.6" },
      { key: "pv", label: "Plastic Viscosity", unit: "cP", placeholder: "20" },
      { key: "yp", label: "Yield Point", unit: "lb/100ft²", placeholder: "18" },
      { key: "dh", label: "Hole / Csg ID", unit: "in", placeholder: "8.5" },
      { key: "dp", label: "Pipe OD", unit: "in", placeholder: "5" },
      { key: "length", label: "Annulus Length", unit: "ft", placeholder: "10000" },
      { key: "flowRate", label: "Flow Rate", unit: "gpm", placeholder: "400" },
    ],
    fn: C.annularPressureLoss,
    to: [{ id: "ecd", fieldKey: "annPressureLoss" }],
  },
  {
    id: "surge_swab", category: "hydraulics", name: "Surge / Swab Pressure",
    description: "Simplified Burkhardt-clinging estimate of surge or swab pressure from pipe movement.",
    fields: [
      { key: "mode", label: "Mode", unit: "", type: "select", options: ["surge", "swab"] },
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "9.6" },
      { key: "pipeVelocity", label: "Pipe Velocity", unit: "ft/min", placeholder: "180" },
      { key: "dh", label: "Hole / Csg ID", unit: "in", placeholder: "8.5" },
      { key: "dp", label: "Pipe OD", unit: "in", placeholder: "5" },
      { key: "length", label: "Length", unit: "ft", placeholder: "10000" },
      { key: "pv", label: "Plastic Viscosity", unit: "cP", placeholder: "20" },
      { key: "yp", label: "Yield Point", unit: "lb/100ft²", placeholder: "18" },
    ],
    fn: C.surgeSwabPressure,
  },
  {
    id: "bit_hhp", category: "hydraulics", name: "Bit Hydraulic Horsepower",
    description: "Pressure drop across the bit, HHP and HSI (if bit diameter provided). Enter nozzles as comma-separated 32nds.",
    fields: [
      { key: "flowRate", label: "Flow Rate", unit: "gpm", placeholder: "e.g. 500" },
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "9.6" },
      { key: "nozzles", label: "Nozzles", unit: "32nds (csv)", type: "text", placeholder: "12,12,12" },
      { key: "bitDiameter", label: "Bit Diameter (optional)", unit: "in", placeholder: "8.5", optional: true, libraryKind: ["bit"] },
    ],
    fn: C.bitHHP,
  },

  // DIRECTIONAL
  {
    id: "dls", category: "directional", name: "Dogleg Severity (DLS)",
    description: "Curvature of the wellbore between two survey stations (deg/100 ft).",
    fields: [
      { key: "inc1", label: "Inclination 1", unit: "°", placeholder: "e.g. 8.5" },
      { key: "az1", label: "Azimuth 1", unit: "°", placeholder: "e.g. 154" },
      { key: "inc2", label: "Inclination 2", unit: "°", placeholder: "e.g. 11.2" },
      { key: "az2", label: "Azimuth 2", unit: "°", placeholder: "e.g. 158" },
      { key: "deltaMd", label: "ΔMD between stations", unit: "ft", placeholder: "e.g. 95" },
      { key: "bhaTool", label: "BHA tool (optional)", unit: "", type: "text", placeholder: "e.g. motor_6.75_1.0bend", optional: true, libraryKind: ["mud_motor", "rotary_steerable"] },
    ],
    fn: C.doglegSeverity,
  },
  {
    id: "md_to_tvd", category: "directional", name: "MD → TVD (Average Angle)",
    description: "Convert measured-depth segment to TVD using the average-angle method.",
    fields: [
      { key: "inc1", label: "Inclination 1", unit: "°", placeholder: "e.g. 8.5" },
      { key: "inc2", label: "Inclination 2", unit: "°", placeholder: "e.g. 11.2" },
      { key: "deltaMd", label: "ΔMD", unit: "ft", placeholder: "e.g. 95" },
    ],
    fn: C.mdToTvd,
  },
  {
    id: "build_rate", category: "directional", name: "Build / Drop Rate",
    description: "Rate of inclination change between two stations.",
    fields: [
      { key: "inc1", label: "Inclination 1", unit: "°", placeholder: "8.5" },
      { key: "inc2", label: "Inclination 2", unit: "°", placeholder: "11.2" },
      { key: "deltaMd", label: "ΔMD", unit: "ft", placeholder: "95" },
      { key: "bhaTool", label: "BHA tool (optional)", unit: "", type: "text", placeholder: "e.g. motor_6.75_1.0bend", optional: true, libraryKind: ["mud_motor", "rotary_steerable"] },
    ],
    fn: C.buildRate,
  },
  {
    id: "horizontal_displacement", category: "directional", name: "Horizontal Displacement",
    description: "North/East/Horizontal displacement using the minimum-curvature method.",
    fields: [
      { key: "inc1", label: "Inclination 1", unit: "°", placeholder: "8.5" },
      { key: "az1", label: "Azimuth 1", unit: "°", placeholder: "154" },
      { key: "inc2", label: "Inclination 2", unit: "°", placeholder: "11.2" },
      { key: "az2", label: "Azimuth 2", unit: "°", placeholder: "158" },
      { key: "deltaMd", label: "ΔMD", unit: "ft", placeholder: "95" },
    ],
    fn: C.horizontalDisplacement,
  },

  // WORKOVER & COMPLETION
  {
    id: "tubing_stretch_free", category: "workover", name: "Tubing Stretch — Free Hanging",
    description: "Elastic stretch of tubing under its own weight.",
    fields: [
      { key: "tubingWeight", label: "Tubing Weight", unit: "lb/ft", placeholder: "e.g. 6.5" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 9000" },
      { key: "area", label: "Pipe Cross Area", unit: "in²", placeholder: "e.g. 1.812" },
    ],
    fn: C.tubingStretchFree,
  },
  {
    id: "tubing_stretch_hookload", category: "workover", name: "Tubing Stretch — Hook Load",
    description: "Elastic stretch under a top-applied tension (e.g. picking up off slips).",
    fields: [
      { key: "pullForce", label: "Pull Force", unit: "lbf", placeholder: "e.g. 60000" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 9000" },
      { key: "area", label: "Pipe Cross Area", unit: "in²", placeholder: "e.g. 1.812" },
    ],
    fn: C.tubingStretchHookLoad,
  },
  {
    id: "bhp_from_whp", category: "workover", name: "Bottom-Hole Pressure from WHP",
    description: "BHP = surface pressure + fluid column hydrostatic.",
    fields: [
      { key: "whp", label: "Wellhead Pressure", unit: "psi", placeholder: "e.g. 1200" },
      { key: "mw", label: "Fluid Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 9.0" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 8500" },
    ],
    fn: C.bhpFromWHP,
  },
  {
    id: "kill_fluid_weight_workover", category: "workover", name: "Workover Kill Fluid Weight",
    description: "Required kill fluid weight for a known reservoir pressure with safety margin.",
    fields: [
      { key: "reservoirPressure", label: "Reservoir Pressure", unit: "psi", placeholder: "e.g. 3500" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 8500" },
      { key: "safetyMargin", label: "Safety Margin", unit: "ppg", placeholder: "e.g. 0.3", optional: true },
    ],
    fn: C.killFluidWeightWorkover,
  },
  {
    id: "perforation_pressure", category: "workover", name: "Perforation Pressure",
    description: "Estimated pressure at the perforations.",
    fields: [
      { key: "surfacePressure", label: "Surface Pressure", unit: "psi", placeholder: "e.g. 0" },
      { key: "mw", label: "Fluid Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "9.0" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "8500" },
      { key: "frictionLoss", label: "Friction Loss", unit: "psi", placeholder: "0", optional: true },
    ],
    fn: C.perforationPressure,
  },

  // ===== EXPANSION PACK v1.4 — new entries (existing calculators above are unchanged) =====

  // DRILLING — additions
  {
    id: "drill_string_volume", category: "drilling", name: "Total Drill String Internal Volume (Single Section)",
    description: "Internal volume of a single-section drill-string column from ID and length. For a multi-component string, use Multi-Section Drill String Internal Volume.",
    fields: [
      { key: "id", label: "Drill String ID", unit: "in", placeholder: "e.g. 4.276", libraryKind: ["drill_pipe", "hwdp", "drill_collar", "tubing"] },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 9500" },
    ],
    fn: C.drillStringVolume,
    to: [
      { id: "strokes_to_bit", fieldKey: "pipeVolume" },
    ],
  },
  {
    id: "multi_section_drillstring_volume", category: "drilling", name: "Multi-Section Drill String Internal Volume",
    description: "Total internal volume of a multi-component drill string. Add one row per component (Drill Pipe, HWDP, Drill Collar, BHA, Other) using the table below. Each row supports a library picker for the tubular ID, with manual override allowed.",
    fields: [
      {
        key: "sections",
        label: "Components",
        unit: "",
        type: "sectionTable",
        sectionTableSpec: {
          mode: "drillstring",
          tubularKinds: ["drill_pipe", "hwdp", "drill_collar", "tubing"],
        },
      },
    ],
    fn: C.multiSectionDrillStringVolume,
    to: [
      { id: "strokes_to_bit", fieldKey: "pipeVolume" },
    ],
  },
  {
    id: "multi_section_annular_volume", category: "drilling", name: "Multi-Section Annular Volume",
    description: "Total annular volume across multiple well sections (different casing IDs / open-hole sizes and different tubulars). Add one row per section using the table below. Each row supports library pickers for the tubular OD and the hole/casing ID, with manual override allowed.",
    fields: [
      {
        key: "sections",
        label: "Sections",
        unit: "",
        type: "sectionTable",
        sectionTableSpec: {
          tubularKinds: ["drill_pipe", "hwdp", "drill_collar"],
          outerKindsCasing: ["casing"],
          outerKindsOpenHole: ["bit"],
        },
      },
    ],
    fn: C.multiSectionAnnularVolume,
    to: [
      { id: "bottoms_up_strokes", fieldKey: "annularVolume" },
      { id: "bottoms_up_time", fieldKey: "annularVolume" },
      { id: "lag_time", fieldKey: "annularVolume" },
    ],
  },
  {
    id: "rop", category: "drilling", name: "Rate of Penetration (ROP)",
    description: "Average ROP from feet drilled over time.",
    fields: [
      { key: "feetDrilled", label: "Feet Drilled", unit: "ft", placeholder: "e.g. 240" },
      { key: "hours", label: "Time", unit: "hr", placeholder: "e.g. 8" },
    ],
    fn: C.rateOfPenetration,
  },
  {
    id: "lag_time", category: "drilling", name: "Lag / Bottoms-Up Time",
    description: "Time for cuttings/fluid from the bit to reach surface — i.e. bottoms-up time. Identical concept to Bottoms-Up Time; either entry returns the same value.",
    fields: [
      { key: "annularVolume", label: "Total Annular Volume (bit→surface)", unit: "bbl", placeholder: "e.g. 350" },
      { key: "flowRate", label: "Flow Rate", unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.lagTime,
  },
  {
    id: "drill_pipe_pressure_drop", category: "drilling", name: "Drill Pipe Pressure Drop",
    description: "Simplified / Requires Verification — laminar Bingham-plastic estimate for pressure loss inside drill pipe. Verify regime; turbulent flow requires the full hydraulics model.",
    fields: [
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 9.6" },
      { key: "pv", label: "Plastic Viscosity", unit: "cP", placeholder: "e.g. 20" },
      { key: "yp", label: "Yield Point", unit: "lb/100ft²", placeholder: "e.g. 18" },
      { key: "flowRate", label: "Flow Rate", unit: "gpm", placeholder: "e.g. 400" },
      { key: "pipeId", label: "Pipe ID", unit: "in", placeholder: "e.g. 4.276" },
      { key: "pipeLength", label: "Pipe Length", unit: "ft", placeholder: "e.g. 9500" },
    ],
    fn: C.drillPipePressureDrop,
  },
  {
    id: "mse", category: "drilling", name: "Mechanical Specific Energy (MSE)",
    description: "Teale's MSE — a drilling-performance indicator estimating the mechanical energy required to remove a unit volume of rock. Advanced / Requires Interpretation. Interpretation depends on bit type, formation, drilling mode, measurement quality, vibration, hydraulics, ROP, WOB, torque, and operating context. Trends are usually more useful than single values.",
    fields: [
      { key: "wob", label: "Weight on Bit (WOB)", unit: "lbf", placeholder: "e.g. 25000" },
      { key: "bitDiameter", label: "Bit Diameter", unit: "in", placeholder: "e.g. 8.5" },
      { key: "rpm", label: "Surface RPM", unit: "", placeholder: "e.g. 120" },
      { key: "torque", label: "Surface Torque", unit: "ft-lbf", placeholder: "e.g. 8000" },
      { key: "rop", label: "ROP", unit: "ft/hr", placeholder: "e.g. 45" },
    ],
    fn: C.mse,
  },
  {
    id: "hole_capacity_per_foot", category: "drilling", name: "Hole Capacity Per Foot",
    description: "Open-hole capacity per linear foot from hole diameter.",
    fields: [
      { key: "diameter", label: "Hole Diameter", unit: "in", placeholder: "e.g. 8.5" },
    ],
    fn: C.holeCapacityPerFoot,
  },
  {
    id: "pipe_capacity_per_foot", category: "drilling", name: "Pipe Capacity Per Foot",
    description: "Internal pipe capacity per linear foot from pipe ID.",
    fields: [
      { key: "id", label: "Pipe ID", unit: "in", placeholder: "e.g. 4.276" },
    ],
    fn: C.pipeCapacityPerFoot,
  },
  {
    id: "annular_capacity_per_foot", category: "drilling", name: "Annular Capacity Per Foot",
    description: "Annular volume per linear foot between hole diameter and pipe OD.",
    fields: [
      { key: "dh", label: "Hole Diameter", unit: "in", placeholder: "e.g. 8.5" },
      { key: "dp", label: "Pipe OD", unit: "in", placeholder: "e.g. 5" },
    ],
    fn: C.annularCapacityPerFoot,
  },
  {
    id: "strokes_for_volume", category: "drilling", name: "Strokes Required for Selected Volume",
    description: "Number of pump strokes to deliver a target volume at a known pump output.",
    fields: [
      { key: "volume", label: "Volume", unit: "bbl", placeholder: "e.g. 120" },
      { key: "pumpBblStk", label: "Pump Output", unit: "bbl/stk", placeholder: "e.g. 0.105", libraryKind: ["pump"] },
    ],
    fn: C.strokesForVolume,
  },
  {
    id: "volume_pumped_from_strokes", category: "drilling", name: "Volume Pumped from Strokes",
    description: "Volume delivered from a stroke count and pump output.",
    fields: [
      { key: "strokes", label: "Strokes", unit: "strokes", placeholder: "e.g. 1200" },
      { key: "pumpBblStk", label: "Pump Output", unit: "bbl/stk", placeholder: "e.g. 0.105", libraryKind: ["pump"] },
    ],
    fn: C.volumePumpedFromStrokes,
  },
  {
    id: "pump_rate_from_spm", category: "drilling", name: "Pump Rate from SPM",
    description: "Pump rate (bbl/min) from pump output and strokes per minute.",
    fields: [
      { key: "pumpBblStk", label: "Pump Output", unit: "bbl/stk", placeholder: "e.g. 0.105", libraryKind: ["pump"] },
      { key: "spm", label: "Strokes Per Minute", unit: "", placeholder: "e.g. 80" },
    ],
    fn: C.pumpRateFromSpm,
  },
  {
    id: "time_to_pump_volume", category: "drilling", name: "Time to Pump Selected Volume",
    description: "Time required to deliver a target volume at a known pump rate.",
    fields: [
      { key: "volume", label: "Volume", unit: "bbl", placeholder: "e.g. 120" },
      { key: "pumpRate", label: "Pump Rate", unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.timeToPumpVolume,
  },
  {
    id: "full_circulation_time", category: "drilling", name: "Full Circulation Time",
    description: "Time to circulate the total active system at a known pump rate.",
    fields: [
      { key: "totalVolume", label: "Total Circulating Volume", unit: "bbl", placeholder: "e.g. 450" },
      { key: "pumpRate", label: "Pump Rate", unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.fullCirculationTime,
  },
  {
    id: "bottoms_up_time", category: "drilling", name: "Bottoms-Up Time",
    description: "Time to circulate the annular contents from bottom to surface (same concept as Lag Time). Pair with Multi-Section Annular Volume for a multi-section string.",
    fields: [
      { key: "annularVolume", label: "Total Annular Volume", unit: "bbl", placeholder: "e.g. 300" },
      { key: "pumpRate", label: "Pump Rate", unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.bottomsUpTime,
  },
  {
    id: "total_circulation_strokes", category: "drilling", name: "Total Circulation Strokes",
    description: "Sum of drill-string strokes and annular strokes for a full circulation.",
    fields: [
      { key: "drillStringStrokes", label: "Drill String Strokes", unit: "strokes", placeholder: "e.g. 1143" },
      { key: "annularStrokes", label: "Annular Strokes", unit: "strokes", placeholder: "e.g. 2857" },
    ],
    fn: C.totalCirculationStrokes,
  },
  {
    id: "mud_weight_from_pressure", category: "drilling", name: "Mud Weight from Pressure and TVD",
    description: "Mud weight equivalent of a pressure column at a given TVD.",
    fields: [
      { key: "pressure", label: "Pressure", unit: "psi", placeholder: "e.g. 5200" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
    ],
    fn: C.mudWeightFromPressure,
  },
  {
    id: "tvd_from_pressure", category: "drilling", name: "Mud Column Height Required for Pressure",
    description: "Estimates the vertical mud column height required to generate a selected hydrostatic pressure at a selected mud weight. It does not determine actual well TVD.",
    fields: [
      { key: "pressure", label: "Pressure", unit: "psi", placeholder: "e.g. 5200" },
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 10.0" },
    ],
    fn: C.tvdFromPressure,
  },
  {
    id: "equivalent_mud_weight", category: "drilling", name: "Equivalent Mud Weight from Pressure",
    description: "Converts a known pressure (psi) at TVD into the equivalent mud weight (ppg). Generic conversion — for well-control / formation-pressure use, prefer the well-control variant.",
    fields: [
      { key: "pressure", label: "Pressure", unit: "psi", placeholder: "e.g. 4680" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 9000" },
    ],
    fn: C.equivalentMudWeight,
  },
  {
    id: "pressure_from_mw_increase", category: "drilling", name: "Pressure from Mud Weight Increase",
    description: "Pressure (psi) equivalent to a mud-weight increase (ppg) over TVD.",
    fields: [
      { key: "mwIncrease", label: "Mud Weight Increase", unit: "ppg", placeholder: "e.g. 1.0" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
    ],
    fn: C.pressureFromMudWeightIncrease,
  },
  {
    id: "mw_increase_for_pressure", category: "drilling", name: "Mud Weight Increase Required for Pressure",
    description: "Mud-weight increase (ppg) required to add a target pressure at TVD.",
    fields: [
      { key: "pressureIncrease", label: "Pressure Increase", unit: "psi", placeholder: "e.g. 520" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
    ],
    fn: C.mudWeightIncreaseForPressure,
  },
  {
    id: "time_to_drill_interval", category: "drilling", name: "Time to Drill Interval",
    description: "Time to drill an interval at a given average ROP.",
    fields: [
      { key: "interval", label: "Interval", unit: "ft", placeholder: "e.g. 1000" },
      { key: "rop", label: "ROP", unit: "ft/hr", placeholder: "e.g. 50" },
    ],
    fn: C.timeToDrillInterval,
  },
  {
    id: "depth_drilled_from_rop", category: "drilling", name: "Depth Drilled from ROP and Time",
    description: "Footage drilled given an average ROP and elapsed time.",
    fields: [
      { key: "rop", label: "ROP", unit: "ft/hr", placeholder: "e.g. 50" },
      { key: "time", label: "Time", unit: "hr", placeholder: "e.g. 20" },
    ],
    fn: C.depthDrilledFromRop,
  },
  {
    id: "connection_time_total", category: "drilling", name: "Connection Time Total",
    description: "Total connection time over a section from number of connections and average connection time.",
    fields: [
      { key: "connections", label: "Number of Connections", unit: "", placeholder: "e.g. 40" },
      { key: "avgConnectionMin", label: "Average Connection Time", unit: "min", placeholder: "e.g. 10" },
    ],
    fn: C.connectionTimeTotal,
  },
  {
    id: "drilling_efficiency", category: "drilling", name: "Drilling Efficiency",
    description: "Percent of total time spent in net drilling (rotating + sliding on bottom).",
    fields: [
      { key: "netDrillingHours", label: "Net Drilling Time", unit: "hr", placeholder: "e.g. 18" },
      { key: "totalHours", label: "Total Time", unit: "hr", placeholder: "e.g. 24" },
    ],
    fn: C.drillingEfficiency,
  },
  {
    id: "feet_per_connection", category: "drilling", name: "Feet Per Connection",
    description: "Average footage drilled between connections.",
    fields: [
      { key: "totalFeet", label: "Total Feet Drilled", unit: "ft", placeholder: "e.g. 1200" },
      { key: "connections", label: "Number of Connections", unit: "", placeholder: "e.g. 40" },
    ],
    fn: C.feetPerConnection,
  },

  // WELL CONTROL — additions
  {
    id: "pit_gain_conversion", category: "wellcontrol", name: "Pit Gain Conversion",
    description: "Convert pit gain between gallons and barrels.",
    fields: [
      { key: "direction", label: "Direction", unit: "", type: "select", options: ["gal_to_bbl", "bbl_to_gal"] },
      { key: "value", label: "Value", unit: "", placeholder: "e.g. 84" },
    ],
    fn: C.pitGainConversion,
  },
  {
    id: "trip_margin", category: "wellcontrol", name: "Trip Margin",
    description: "Pressure equivalent of a trip margin (ppg) over TVD.",
    wellControl: true,
    fields: [
      { key: "tripMarginPpg", label: "Trip Margin", unit: "ppg", placeholder: "e.g. 0.3" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 12000" },
    ],
    fn: C.tripMargin,
  },
  {
    id: "hydrostatic_at_shoe", category: "wellcontrol", name: "Hydrostatic Pressure at Shoe",
    description: "Hydrostatic pressure of the mud column at the casing shoe.",
    wellControl: true,
    fields: [
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 10.0" },
      { key: "shoeTvd", label: "Shoe TVD", unit: "ft", rcePath: "well.casingShoeTvdFt", placeholder: "e.g. 8000" },
    ],
    fn: C.hydrostaticAtShoe,
  },
  {
    id: "formation_pressure_current_mw", category: "wellcontrol", name: "Formation Pressure from SIDPP (Current MW)",
    description: "Formation pressure from the current mud-weight hydrostatic plus the recorded SIDPP.",
    wellControl: true,
    fields: [
      { key: "currentMW", label: "Current Mud Weight", unit: "ppg", placeholder: "e.g. 10.0", rcePath: "mud.weightInPpg" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
      { key: "sidpp", label: "SIDPP", unit: "psi", placeholder: "e.g. 500" },
    ],
    fn: C.formationPressureFromCurrentMW,
  },
  {
    id: "emw_from_formation_pressure", category: "wellcontrol", name: "Equivalent Mud Weight from Formation Pressure",
    description: "Equivalent mud weight (ppg) that balances a known formation pressure at TVD.",
    wellControl: true,
    fields: [
      { key: "formationPressure", label: "Formation Pressure", unit: "psi", placeholder: "e.g. 5700" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
    ],
    fn: C.emwFromFormationPressure,
  },
  {
    id: "trip_margin_pressure", category: "wellcontrol", name: "Trip Margin Pressure",
    description: "Pressure (psi) equivalent of a desired trip margin (ppg) at TVD.",
    wellControl: true,
    fields: [
      { key: "tripMarginPpg", label: "Trip Margin", unit: "ppg", placeholder: "e.g. 0.5" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
    ],
    fn: C.tripMarginPressure,
  },
  {
    id: "required_trip_margin_mw", category: "wellcontrol", name: "Required Trip Margin Mud Weight",
    description: "Mud-weight increment (ppg) required to produce a desired trip margin pressure at TVD.",
    wellControl: true,
    fields: [
      { key: "desiredPressure", label: "Desired Trip Margin Pressure", unit: "psi", placeholder: "e.g. 260" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
    ],
    fn: C.requiredTripMarginMW,
  },
  {
    id: "shutin_pressure_difference", category: "wellcontrol", name: "Shut-In Pressure Difference",
    description: "SICP minus SIDPP — useful as an annular-vs-string pressure comparison after shut-in.",
    wellControl: true,
    fields: [
      { key: "sicp", label: "SICP", unit: "psi", placeholder: "e.g. 900" },
      { key: "sidpp", label: "SIDPP", unit: "psi", placeholder: "e.g. 500" },
    ],
    fn: C.shutInPressureDifference,
  },
  {
    id: "pit_gain_bbl_from_gal", category: "wellcontrol", name: "Pit Gain Barrels from Gallons",
    description: "Convert pit-gain volume from gallons to barrels.",
    wellControl: true,
    fields: [
      { key: "gallons", label: "Pit Gain", unit: "gal", placeholder: "e.g. 420" },
    ],
    fn: C.pitGainBblFromGal,
  },
  {
    id: "pit_gain_gal_from_bbl", category: "wellcontrol", name: "Pit Gain Gallons from Barrels",
    description: "Convert pit-gain volume from barrels to gallons.",
    wellControl: true,
    fields: [
      { key: "barrels", label: "Pit Gain", unit: "bbl", placeholder: "e.g. 10" },
    ],
    fn: C.pitGainGalFromBbl,
  },
  {
    id: "influx_height_estimate", category: "wellcontrol", name: "Influx Height Estimate",
    description: "Simplified estimate of influx column height from pit gain and annular capacity.",
    wellControl: true,
    fields: [
      { key: "pitGain", label: "Pit Gain", unit: "bbl", placeholder: "e.g. 10" },
      { key: "annularCapacity", label: "Annular Capacity", unit: "bbl/ft", placeholder: "e.g. 0.05" },
    ],
    fn: C.influxHeightEstimate,
  },
  {
    id: "kill_mud_weight_increase", category: "wellcontrol", name: "Kill Mud Weight Increase",
    description: "Mud-weight increase (ppg) required to balance SIDPP at TVD.",
    wellControl: true,
    fields: [
      { key: "sidpp", label: "SIDPP", unit: "psi", placeholder: "e.g. 500" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "e.g. 10000" },
    ],
    fn: C.killMudWeightIncrease,
  },

  // =========================================================================
  // Subsea Stack Well Control — Batch 1
  // Every entry below carries stackMode: "subsea" so it appears ONLY under the
  // Subsea Stack tab on /categories/wellcontrol and shows the red subsea
  // safety banner via CalculatorRunner.
  // =========================================================================
  {
    id: "marine_riser_capacity", category: "wellcontrol", name: "Marine Riser Capacity",
    description: "Internal capacity of the marine riser between RKB and the subsea BOP.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "riserId",     label: "Riser ID",     unit: "in", placeholder: "e.g. 19" },
      { key: "riserLength", label: "Riser Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.marineRiserCapacity,
    to: [
      { id: "riser_circulation_volume", fieldKey: "riserCapacity" },
    ],
  },
  {
    id: "choke_line_capacity", category: "wellcontrol", name: "Choke Line Capacity",
    description: "Internal volume of the choke line from manifold to subsea BOP.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "chokeLineId",     label: "Choke Line ID",     unit: "in", placeholder: "e.g. 3" },
      { key: "chokeLineLength", label: "Choke Line Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.chokeLineCapacity,
    to: [
      { id: "total_ck_volume", fieldKey: "chokeVolume" },
    ],
  },
  {
    id: "kill_line_capacity", category: "wellcontrol", name: "Kill Line Capacity",
    description: "Internal volume of the kill line from rig pumps to subsea BOP.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "killLineId",     label: "Kill Line ID",     unit: "in", placeholder: "e.g. 3" },
      { key: "killLineLength", label: "Kill Line Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.killLineCapacity,
    to: [
      { id: "total_ck_volume", fieldKey: "killVolume" },
    ],
  },
  {
    id: "total_ck_volume", category: "wellcontrol", name: "Total Choke and Kill Line Volume",
    description: "Sum of choke-line and kill-line internal volumes for circulation planning.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "chokeVolume", label: "Choke Line Volume", unit: "bbl", placeholder: "e.g. 43.71" },
      { key: "killVolume",  label: "Kill Line Volume",  unit: "bbl", placeholder: "e.g. 43.71" },
    ],
    fn: C.totalChokeKillVolume,
  },
  {
    id: "riser_circulation_volume", category: "wellcontrol", name: "Riser Circulation Volume",
    description: "Marine-riser internal volume carried through as the riser-circulation reference volume.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "riserCapacity", label: "Riser Capacity", unit: "bbl", placeholder: "e.g. 1753.45" },
    ],
    fn: C.riserCirculationVolume,
  },
  {
    id: "riser_hydrostatic_pressure", category: "wellcontrol", name: "Riser Hydrostatic Pressure",
    description: "Hydrostatic pressure of the mud column inside the marine riser from RKB to the subsea BOP.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "mw",       label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 10.0" },
      { key: "riserTvd", label: "Riser TVD",  unit: "ft",  placeholder: "e.g. 5000" },
    ],
    fn: C.riserHydrostaticPressure,
    to: [
      { id: "differential_riser_hydrostatic", fieldKey: "mudHydrostatic" },
    ],
  },
  {
    id: "seawater_hydrostatic_pressure", category: "wellcontrol", name: "Seawater Hydrostatic Pressure",
    description: "Seawater hydrostatic pressure at the BOP, using a typical 0.445 psi/ft gradient (verify against site-specific seawater density and temperature).",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "waterDepth", label: "Water Depth", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.seawaterHydrostaticPressure,
    to: [
      { id: "differential_riser_hydrostatic", fieldKey: "seawaterHydrostatic" },
    ],
  },
  {
    id: "differential_riser_hydrostatic", category: "wellcontrol", name: "Differential Riser Hydrostatic Pressure",
    description: "Mud-column riser pressure minus seawater pressure — the loss of bottom-hole pressure if the riser is disconnected.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "mudHydrostatic",       label: "Mud Hydrostatic Pressure",      unit: "psi", placeholder: "e.g. 2600" },
      { key: "seawaterHydrostatic",  label: "Seawater Hydrostatic Pressure", unit: "psi", placeholder: "e.g. 2225" },
    ],
    fn: C.differentialRiserHydrostatic,
  },
  {
    id: "riser_margin_pressure", category: "wellcontrol", name: "Riser Margin Pressure",
    description: "Pressure equivalent (psi) of a riser margin (ppg) over the water depth.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "riserMarginPpg", label: "Riser Margin", unit: "ppg", placeholder: "e.g. 0.5" },
      { key: "waterDepth",     label: "Water Depth",  unit: "ft",  placeholder: "e.g. 5000" },
    ],
    fn: C.riserMarginPressure,
  },
  {
    id: "required_riser_margin_mw", category: "wellcontrol", name: "Required Riser Margin Mud Weight",
    description: "Mud-weight increment (ppg) required to produce a desired riser-margin pressure at the water depth.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "desiredPressure", label: "Desired Pressure Margin", unit: "psi", placeholder: "e.g. 130" },
      { key: "waterDepth",      label: "Water Depth",             unit: "ft",  placeholder: "e.g. 5000" },
    ],
    fn: C.requiredRiserMarginMW,
  },
  {
    id: "choke_line_friction_record", category: "wellcontrol", name: "Choke Line Friction Record",
    description: "Record-keeping worksheet for measured choke-line friction at the stated test conditions. NOT a prediction calculator.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "pumpRate",         label: "Pump Rate",                 unit: "spm",  placeholder: "e.g. 30" },
      { key: "mw",               label: "Mud Weight",                unit: "ppg", rcePath: "mud.weightInPpg",  placeholder: "e.g. 10.0" },
      { key: "chokeLineId",      label: "Choke Line ID",             unit: "in",   placeholder: "e.g. 3" },
      { key: "chokeLineLength",  label: "Choke Line Length",         unit: "ft",   placeholder: "e.g. 5000" },
      { key: "measuredFriction", label: "Measured Friction Pressure",unit: "psi",  placeholder: "e.g. 250" },
      { key: "testDateTime",     label: "Test Date/Time",            unit: "",     placeholder: "YYYY-MM-DD HH:MM", optional: true },
      { key: "notes",            label: "Notes",                     unit: "",     type: "text", placeholder: "Pump #1, choke 1, …", optional: true },
    ],
    fn: C.chokeLineFrictionRecord,
  },
  {
    id: "kill_line_friction_record", category: "wellcontrol", name: "Kill Line Friction Record",
    description: "Record-keeping worksheet for measured kill-line friction at the stated test conditions. NOT a prediction calculator.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "pumpRate",         label: "Pump Rate",                 unit: "spm",  placeholder: "e.g. 30" },
      { key: "mw",               label: "Mud Weight",                unit: "ppg", rcePath: "mud.weightInPpg",  placeholder: "e.g. 10.0" },
      { key: "killLineId",       label: "Kill Line ID",              unit: "in",   placeholder: "e.g. 3" },
      { key: "killLineLength",   label: "Kill Line Length",          unit: "ft",   placeholder: "e.g. 5000" },
      { key: "measuredFriction", label: "Measured Friction Pressure",unit: "psi",  placeholder: "e.g. 250" },
      { key: "testDateTime",     label: "Test Date/Time",            unit: "",     placeholder: "YYYY-MM-DD HH:MM", optional: true },
      { key: "notes",            label: "Notes",                     unit: "",     type: "text", placeholder: "Pump #2, kill line, …", optional: true },
    ],
    fn: C.killLineFrictionRecord,
  },
  {
    id: "scr_with_choke_friction", category: "wellcontrol", name: "SCR with Choke Line Friction",
    description: "Adjusted circulating pressure = SCR pressure + measured choke-line friction. Use only if it matches approved company subsea procedure.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "scrPressure",       label: "SCR Pressure",          unit: "psi", placeholder: "e.g. 600" },
      { key: "chokeLineFriction", label: "Choke Line Friction",   unit: "psi", placeholder: "e.g. 250" },
    ],
    fn: C.scrWithChokeFriction,
    to: [
      { id: "subsea_icp_support", fieldKey: "scrPressure" },
      { id: "subsea_fcp_support", fieldKey: "scrPressure" },
    ],
  },
  {
    id: "subsea_icp_support", category: "wellcontrol", name: "Subsea ICP Support",
    description: "Worksheet support: Subsea ICP = SIDPP + SCR + Choke Line Friction. Company procedure may handle CLF differently.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "sidpp",             label: "SIDPP",                 unit: "psi", placeholder: "e.g. 500" },
      { key: "scrPressure",       label: "SCR Pressure",          unit: "psi", placeholder: "e.g. 600" },
      { key: "chokeLineFriction", label: "Choke Line Friction",   unit: "psi", placeholder: "e.g. 250" },
    ],
    fn: C.subseaIcpSupport,
  },
  {
    id: "subsea_fcp_support", category: "wellcontrol", name: "Subsea FCP Support",
    description: "Worksheet support: Subsea FCP = SCR × (KMW / OMW) + Choke Line Friction. Company procedure may handle CLF differently.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "scrPressure",       label: "SCR Pressure",          unit: "psi", placeholder: "e.g. 600" },
      { key: "killMW",            label: "Kill Mud Weight",       unit: "ppg", placeholder: "e.g. 11.0" },
      { key: "originalMW",        label: "Original Mud Weight",   unit: "ppg", placeholder: "e.g. 10.0" },
      { key: "chokeLineFriction", label: "Choke Line Friction",   unit: "psi", placeholder: "e.g. 250" },
    ],
    fn: C.subseaFcpSupport,
  },
  {
    id: "subsea_kill_sheet_worksheet", category: "wellcontrol", name: "Subsea Kill Sheet Worksheet Support",
    description: "Draft worksheet for certified review only. Multiple deterministic sub-calculations (Kill MW, Subsea ICP, Subsea FCP, strokes, circulation time). Inputs are optional — outputs are only computed where their required inputs are present. NOT a live well-control instruction system.",
    wellControl: true,
    stackMode: "subsea",
    prefillAction: "subseaProfile",
    fields: [
      { key: "rigName",            label: "Rig name",                                       unit: "",         type: "text", optional: true, placeholder: "e.g. Drillship Alpha" },
      { key: "wellName",           label: "Well name",                                      unit: "",         type: "text", optional: true, placeholder: "e.g. GoM-22A" },
      { key: "waterDepth",         label: "Water depth",                                    unit: "ft",       optional: true, placeholder: "e.g. 5000" },
      { key: "bopStackDepth",      label: "BOP stack depth",                                unit: "ft",       optional: true, placeholder: "e.g. 5080" },
      { key: "rkbToMudline",       label: "RKB to mudline",                                 unit: "ft",       optional: true, placeholder: "e.g. 5080" },
      { key: "currentMW",          label: "Current mud weight",                             unit: "ppg",      optional: true, placeholder: "e.g. 10.0", rcePath: "mud.weightInPpg" },
      { key: "killMW",             label: "Kill mud weight (override — optional)",          unit: "ppg",      optional: true, placeholder: "computed from CMW+SIDPP+TVD" },
      { key: "sidpp",              label: "SIDPP",                                          unit: "psi",      optional: true, placeholder: "e.g. 500" },
      { key: "sicp",               label: "SICP",                                           unit: "psi",      optional: true, placeholder: "e.g. 900" },
      { key: "pitGain",            label: "Pit gain",                                       unit: "bbl",      optional: true, placeholder: "e.g. 10" },
      { key: "tvd",                label: "TVD",                                            unit: "ft", rcePath: "well.currentTvdFt",       optional: true, placeholder: "e.g. 10000" },
      { key: "scrPressure",        label: "SCR pressure",                                   unit: "psi",      optional: true, placeholder: "e.g. 600" },
      { key: "killRate",           label: "Kill rate",                                      unit: "spm",      optional: true, placeholder: "e.g. 30" },
      { key: "pumpOutput",         label: "Pump output",                                    unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk",  optional: true, placeholder: "e.g. 0.105" },
      { key: "drillStringVol",     label: "Drill string internal volume",                   unit: "bbl",      optional: true, placeholder: "e.g. 168" },
      { key: "annularVol",         label: "Annular volume",                                 unit: "bbl",      optional: true, placeholder: "e.g. 350" },
      { key: "riserVol",           label: "Riser volume",                                   unit: "bbl",      optional: true, placeholder: "e.g. 1753" },
      { key: "chokeLineVol",       label: "Choke line volume",                              unit: "bbl",      optional: true, placeholder: "e.g. 44" },
      { key: "killLineVol",        label: "Kill line volume",                               unit: "bbl",      optional: true, placeholder: "e.g. 44" },
      { key: "chokeFriction",      label: "Measured choke line friction",                   unit: "psi",      optional: true, placeholder: "e.g. 250" },
      { key: "killFriction",       label: "Measured kill line friction",                    unit: "psi",      optional: true, placeholder: "e.g. 250" },
      { key: "chokeFrictionRate",  label: "Choke line friction test rate",                  unit: "spm",      optional: true, placeholder: "e.g. 30" },
      { key: "killFrictionRate",   label: "Kill line friction test rate",                   unit: "spm",      optional: true, placeholder: "e.g. 30" },
      { key: "casingShoeTvd",      label: "Casing shoe TVD",                                unit: "ft", rcePath: "well.casingShoeTvdFt",       optional: true, placeholder: "e.g. 8000" },
      { key: "selectedVolume",     label: "Selected volume for circulation time",           unit: "bbl",      optional: true, placeholder: "e.g. 450" },
      { key: "lotFitNotes",        label: "LOT / FIT notes",                                unit: "",         type: "text", optional: true, placeholder: "LOT 14.2 ppg @ 8000 ft, …" },
      { key: "procedureRef",       label: "Company procedure reference",                    unit: "",         type: "text", optional: true, placeholder: "e.g. RSC-WC-OP-005" },
      { key: "userNotes",          label: "User notes",                                     unit: "",         type: "text", optional: true, placeholder: "Operator observations…" },
    ],
    fn: C.subseaKillSheetWorksheet,
  },
  {
    id: "subsea_bop_stack_config_record", category: "wellcontrol", name: "Subsea BOP Stack Configuration Record",
    description: "Documentation-support record for a subsea BOP stack. Does NOT certify equipment condition, regulatory compliance, or readiness.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "rigName",           label: "Rig name",                    unit: "",  type: "text", optional: true, placeholder: "e.g. Drillship Alpha" },
      { key: "wellName",          label: "Well name",                   unit: "",  type: "text", optional: true, placeholder: "e.g. GoM-22A" },
      { key: "stackType",         label: "Stack type",                  unit: "",  type: "text", optional: true, placeholder: "e.g. 18-3/4 in 15K" },
      { key: "waterDepth",        label: "Water depth",                 unit: "ft",                optional: true, placeholder: "e.g. 5000" },
      { key: "annularDetails",    label: "Annular preventer details",   unit: "",  type: "text", optional: true, placeholder: "Make/model, working pressure, last test…" },
      { key: "ramConfig",         label: "Ram configuration",           unit: "",  type: "text", optional: true, placeholder: "VBR / pipe rams / blind / shear order…" },
      { key: "shearRamDetails",   label: "Shear ram details",           unit: "",  type: "text", optional: true, placeholder: "Make/model, certified shear capability…" },
      { key: "chokeLineDetails",  label: "Choke line details",          unit: "",  type: "text", optional: true, placeholder: "ID, length, working pressure…" },
      { key: "killLineDetails",   label: "Kill line details",           unit: "",  type: "text", optional: true, placeholder: "ID, length, working pressure…" },
      { key: "connectorDetails",  label: "Connector details",           unit: "",  type: "text", optional: true, placeholder: "LMRP / wellhead connector…" },
      { key: "controlPodDetails", label: "Control pod details",         unit: "",  type: "text", optional: true, placeholder: "Pod A / Pod B, MUX/electro-hydraulic…" },
      { key: "rovPanelNotes",     label: "ROV intervention panel notes",unit: "",  type: "text", optional: true, placeholder: "Hot stab functions, last verification…" },
      { key: "acousticBackup",    label: "Acoustic backup notes",       unit: "",  type: "text", optional: true, placeholder: "Acoustic system make/model, last test…" },
      { key: "testPressureNotes", label: "Test pressure notes",         unit: "",  type: "text", optional: true, placeholder: "Working pressures by component…" },
      { key: "lastFunctionDate",  label: "Last function test date",     unit: "",  type: "text", optional: true, placeholder: "YYYY-MM-DD" },
      { key: "lastPressureDate",  label: "Last pressure test date",     unit: "",  type: "text", optional: true, placeholder: "YYYY-MM-DD" },
      { key: "notes",             label: "Notes",                       unit: "",  type: "text", optional: true, placeholder: "Operator observations…" },
    ],
    fn: C.subseaBopStackConfigurationRecord,
  },
  {
    id: "subsea_bop_function_test_log", category: "wellcontrol", name: "Subsea BOP Function Test Log",
    description: "User-entered function test log. Pass/fail is recorded as entered by the user; the app does NOT independently certify this test.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "dateTime",         label: "Date / time",              unit: "", type: "text", optional: true, placeholder: "YYYY-MM-DD HH:MM" },
      { key: "component",        label: "Component",                unit: "", type: "text", optional: true, placeholder: "e.g. Upper Annular" },
      { key: "functionTested",   label: "Function tested",          unit: "", type: "text", optional: true, placeholder: "e.g. Close" },
      { key: "podUsed",          label: "Control system / pod used",unit: "", type: "text", optional: true, placeholder: "e.g. Pod A (Yellow)" },
      { key: "responseTime",     label: "Response time",            unit: "s",  optional: true, placeholder: "e.g. 35" },
      { key: "pressureObserved", label: "Pressure observed",        unit: "psi",optional: true, placeholder: "e.g. 1500" },
      { key: "userResult",       label: "Result (user-entered)",    unit: "", type: "text", optional: true, placeholder: "Pass / Fail / N/A" },
      { key: "witness",          label: "Witness",                  unit: "", type: "text", optional: true, placeholder: "Name / role" },
      { key: "notes",            label: "Notes",                    unit: "", type: "text", optional: true, placeholder: "Operator observations…" },
    ],
    fn: C.subseaBopFunctionTestLog,
  },
  {
    id: "subsea_bop_pressure_test_record", category: "wellcontrol", name: "Subsea BOP Pressure Test Record",
    description: "User-entered pressure test record. Acceptance and pass/fail are recorded as entered by the user; the app does NOT independently certify this test.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "dateTime",            label: "Date / time",                       unit: "",   type: "text", optional: true, placeholder: "YYYY-MM-DD HH:MM" },
      { key: "componentTested",     label: "Component tested",                  unit: "",   type: "text", optional: true, placeholder: "e.g. Blind shear ram" },
      { key: "testPressure",        label: "Test pressure",                     unit: "psi",                optional: true, placeholder: "e.g. 10000" },
      { key: "holdTime",            label: "Hold time",                         unit: "min",                optional: true, placeholder: "e.g. 5" },
      { key: "startPressure",       label: "Starting pressure",                 unit: "psi",                optional: true, placeholder: "e.g. 10000" },
      { key: "endPressure",         label: "Ending pressure",                   unit: "psi",                optional: true, placeholder: "e.g. 9950" },
      { key: "pressureDrop",        label: "Pressure drop (override — optional)",unit: "psi",               optional: true, placeholder: "computed from start − end" },
      { key: "acceptanceCriteria",  label: "Acceptance criteria (user-entered)",unit: "",   type: "text", optional: true, placeholder: "e.g. ≤ 100 psi drop per company procedure" },
      { key: "userResult",          label: "Result (user-entered)",             unit: "",   type: "text", optional: true, placeholder: "Pass / Fail / N/A" },
      { key: "witness",             label: "Witness",                           unit: "",   type: "text", optional: true, placeholder: "Name / role" },
      { key: "notes",               label: "Notes",                             unit: "",   type: "text", optional: true, placeholder: "Operator observations…" },
    ],
    fn: C.subseaBopPressureTestRecord,
  },
  {
    id: "subsea_pod_control_record", category: "wellcontrol", name: "Pod / Control System Record",
    description: "Documentation-support record for the subsea pod/control system. Does NOT certify equipment condition or readiness.",
    wellControl: true,
    stackMode: "subsea",
    fields: [
      { key: "activePod",          label: "Active pod",                   unit: "",   type: "text", optional: true, placeholder: "e.g. Pod A (Yellow)" },
      { key: "backupPod",          label: "Backup pod",                   unit: "",   type: "text", optional: true, placeholder: "e.g. Pod B (Blue)" },
      { key: "functionTested",     label: "Function tested",              unit: "",   type: "text", optional: true, placeholder: "e.g. Annular close" },
      { key: "responseObserved",   label: "Response observed",            unit: "",   type: "text", optional: true, placeholder: "e.g. Within expected time" },
      { key: "hydraulicPressure",  label: "Hydraulic pressure",           unit: "psi",                optional: true, placeholder: "e.g. 3000" },
      { key: "electricalStatus",   label: "Electrical / communication",   unit: "",   type: "text", optional: true, placeholder: "e.g. Comms OK / MUX healthy" },
      { key: "rovBackupNotes",     label: "ROV backup notes",             unit: "",   type: "text", optional: true, placeholder: "Hot stab access, last verification…" },
      { key: "notes",              label: "Notes",                        unit: "",   type: "text", optional: true, placeholder: "Operator observations…" },
    ],
    fn: C.subseaPodControlRecord,
  },
  {
    id: "maasp_worksheet", category: "wellcontrol", name: "MAASP Support Worksheet",
    description: "Computational support for MAASP from LOT/FIT, MW at test, current MW, shoe TVD, and a safety margin.",
    wellControl: true,
    fields: [
      { key: "leakoffPressure", label: "LOT/FIT Pressure", unit: "psi", placeholder: "e.g. 1450" },
      { key: "mwAtTest", label: "Mud Weight at Test", unit: "ppg", placeholder: "e.g. 9.4" },
      { key: "currentMW", label: "Current Mud Weight", unit: "ppg", placeholder: "e.g. 10.2", rcePath: "mud.weightInPpg" },
      { key: "shoeTVD", label: "Shoe TVD", unit: "ft", rcePath: "well.casingShoeTvdFt", placeholder: "e.g. 5500", libraryKind: ["formation_template_section"] },
      { key: "safetyMargin", label: "Safety Margin", unit: "ppg", placeholder: "e.g. 0.3", optional: true },
    ],
    fn: C.maaspWorksheet,
  },
  {
    id: "kick_tolerance_support", category: "wellcontrol", name: "Kick Tolerance / Influx Scenario Support Calculator",
    description: "Two-mode support calculation: Mode A evaluates pressure margin at the shoe for a user-entered influx height; Mode B derives a simplified planning kick tolerance volume from MAASP, MW, influx gradient, shoe TVD, and annular capacity. Documentation support only.",
    wellControl: true,
    fields: [
      { key: "mode", label: "Mode", unit: "", type: "select", options: [
        "Mode A — Influx Scenario Pressure Margin",
        "Mode B — Planning Kick Tolerance Volume",
      ] },
      { key: "maasp", label: "MAASP (required for both modes)", unit: "psi", placeholder: "e.g. 1200" },
      { key: "currentMW", label: "Current Mud Weight (required for both modes)", unit: "ppg", placeholder: "e.g. 12.4", rcePath: "mud.weightInPpg" },
      { key: "shoeTVD", label: "Shoe / Weak-Point TVD (required for both modes)", unit: "ft", rcePath: "well.casingShoeTvdFt", placeholder: "e.g. 8000", libraryKind: ["formation_template_section"] },
      { key: "influxGradient", label: "Influx Gradient (required for both modes)", unit: "psi/ft", placeholder: "e.g. 0.10" },
      { key: "influxHeight", label: "Influx Height — Mode A only", unit: "ft", placeholder: "e.g. 150", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode A"] } },
      { key: "annularCapacityAtShoe", label: "Annular Capacity at Shoe — Mode B required; Mode A optional", unit: "bbl/ft", placeholder: "e.g. 0.045", optional: true },
      { key: "safetyMarginPsi", label: "Safety Margin — Mode B only (defaults to 0 if blank)", unit: "psi", placeholder: "e.g. 100", optional: true,
        visibleWhen: { field: "mode", startsWith: ["Mode B"] } },
    ],
    fn: C.kickToleranceSupport,
  },
  {
    id: "pump_pressure_schedule", category: "wellcontrol", name: "Pump Pressure Schedule (Draft)",
    description: "Draft linear schedule from ICP at stroke 0 to FCP at strokes-to-bit.",
    wellControl: true,
    fields: [
      { key: "icp", label: "ICP", unit: "psi", placeholder: "e.g. 850" },
      { key: "fcp", label: "FCP", unit: "psi", placeholder: "e.g. 720" },
      { key: "strokesToBit", label: "Strokes to Bit", unit: "stk", placeholder: "e.g. 1200" },
      { key: "intervalStrokes", label: "Interval", unit: "stk", placeholder: "100", optional: true },
    ],
    fn: C.pumpPressureSchedule,
  },
  {
    id: "well_kill_worksheet", category: "wellcontrol", name: "Well Kill Worksheet (Draft)",
    description: "Structured composite worksheet — produces KMW, ICP, FCP, and stroke counts for qualified review.",
    wellControl: true,
    fields: [
      { key: "currentMW", label: "Current Mud Weight", unit: "ppg", placeholder: "9.6", rcePath: "mud.weightInPpg" },
      { key: "sidpp", label: "SIDPP", unit: "psi", placeholder: "250" },
      { key: "sicp", label: "SICP", unit: "psi", placeholder: "320" },
      { key: "tvd", label: "TVD", unit: "ft", rcePath: "well.currentTvdFt", placeholder: "12000" },
      { key: "scr", label: "SCR Pressure", unit: "psi", placeholder: "600" },
      { key: "pumpOutput", label: "Pump Output", unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk", placeholder: "0.105" },
      { key: "drillStringCap", label: "Drill String Capacity", unit: "bbl", placeholder: "120" },
      { key: "annularVol", label: "Annular Volume", unit: "bbl", placeholder: "350" },
      { key: "pitGain", label: "Pit Gain", unit: "bbl", placeholder: "12", optional: true },
    ],
    fn: C.wellKillWorksheet,
  },

  // MUD — additions
  {
    id: "chemical_concentration", category: "mud", name: "Chemical Concentration",
    description: "Chemical mass / volume required for a target concentration in the system.",
    fields: [
      { key: "concentration", label: "Desired Concentration", unit: "", placeholder: "e.g. 2.5" },
      { key: "systemVolume", label: "System Volume", unit: "", placeholder: "e.g. 800" },
    ],
    fn: C.chemicalConcentration,
  },
  {
    id: "owr", category: "mud", name: "Oil-Water Ratio (OWR)",
    description: "Compute the OWR percentages from oil and water volumes.",
    fields: [
      { key: "oilVolume", label: "Oil Volume", unit: "", placeholder: "e.g. 70" },
      { key: "waterVolume", label: "Water Volume", unit: "", placeholder: "e.g. 30" },
    ],
    fn: C.oilWaterRatio,
  },
  {
    id: "retort_solids", category: "mud", name: "Retort Solids %",
    description: "Solids percent from retort oil + water readings.",
    fields: [
      { key: "oilRetortPct", label: "Oil (%)", unit: "%", placeholder: "e.g. 35" },
      { key: "waterRetortPct", label: "Water (%)", unit: "%", placeholder: "e.g. 50" },
    ],
    fn: C.retortSolids,
  },
  {
    id: "brine_density_convert", category: "mud", name: "Brine Density Converter",
    description: "Convert a brine density between ppg, SG, and kg/m³.",
    fields: [
      { key: "fromSystem", label: "Input Unit", unit: "", type: "select", options: ["ppg", "sg", "kg/m3"] },
      { key: "value", label: "Value", unit: "", placeholder: "e.g. 10.5" },
    ],
    fn: C.brineDensityConvert,
  },
  {
    id: "mud_cost_bbl", category: "mud", name: "Mud Cost per Barrel",
    description: "Average mud cost per barrel for a job or interval.",
    fields: [
      { key: "totalCost", label: "Total Mud Cost", unit: "$", placeholder: "e.g. 45000" },
      { key: "totalBarrels", label: "Total Barrels", unit: "bbl", placeholder: "e.g. 1200" },
    ],
    fn: C.mudCostPerBarrel,
  },

  // CEMENT — additions
  {
    id: "annular_cement_volume", category: "cement", name: "Annular Cement Volume",
    description: "Required annular cement volume with optional excess.",
    fields: [
      { key: "annularCapacity", label: "Annular Capacity", unit: "bbl/ft", placeholder: "e.g. 0.0427" },
      { key: "intervalLength", label: "Interval Length", unit: "ft", placeholder: "e.g. 2000" },
      { key: "excess", label: "Excess", unit: "%", placeholder: "30", optional: true },
    ],
    fn: C.annularCementVolume,
  },
  {
    id: "top_of_cement", category: "cement", name: "Top of Cement Estimate",
    description: "Estimate top of cement from pumped volume and annular capacity.",
    fields: [
      { key: "cementVolume", label: "Cement Volume", unit: "bbl", placeholder: "e.g. 150" },
      { key: "annularCapacity", label: "Annular Capacity", unit: "bbl/ft", placeholder: "e.g. 0.0427" },
      { key: "shoeDepth", label: "Shoe Depth", unit: "ft", placeholder: "e.g. 8500" },
    ],
    fn: C.topOfCement,
  },

  // CASING & TUBING — additions
  {
    id: "buoyed_weight", category: "casing", name: "Buoyed Weight",
    description: "Pipe weight buoyed in mud. Provide a Buoyancy Factor OR a Mud Weight.",
    fields: [
      { key: "airWeight", label: "Air Weight", unit: "lb", placeholder: "e.g. 240000" },
      { key: "mw", label: "Mud Weight (optional)", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 10.0", optional: true, keepUnit: true },
      { key: "buoyancyFactor", label: "Buoyancy Factor (optional)", unit: "", placeholder: "e.g. 0.847", optional: true },
    ],
    fn: C.buoyedWeight,
  },
  {
    id: "completion_fluid_volume", category: "casing", name: "Completion Fluid Volume",
    description: "Generic volume = capacity × length (use casing/tubing/annular capacity).",
    fields: [
      { key: "capacity", label: "Capacity", unit: "bbl/ft", placeholder: "e.g. 0.01776" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 8000" },
    ],
    fn: C.completionFluidVolume,
  },
  {
    id: "tubing_displacement", category: "casing", name: "Tubing Displacement",
    description: "Volume of steel displaced by tubing run/pulled.",
    fields: [
      { key: "displacementBblFt", label: "Tubing Displacement", unit: "bbl/ft", placeholder: "e.g. 0.00432" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 8000" },
    ],
    fn: C.tubingDisplacement,
  },

  // PRODUCTION — additions
  {
    id: "oil_cut", category: "production", name: "Oil Cut",
    description: "Percentage of oil in the produced liquid stream.",
    fields: [
      { key: "oilBpd", label: "Oil Rate", unit: "bbl/d", placeholder: "e.g. 700" },
      { key: "waterBpd", label: "Water Rate", unit: "bbl/d", placeholder: "e.g. 300" },
    ],
    fn: C.oilCut,
  },
  {
    id: "tank_volume_cyl", category: "production", name: "Tank Volume (Cylindrical)",
    description: "Volume of a vertical cylindrical tank or partial fluid column.",
    fields: [
      { key: "diameter", label: "Tank Diameter", unit: "ft", placeholder: "e.g. 12" },
      { key: "height", label: "Fluid Height", unit: "ft", placeholder: "e.g. 18" },
    ],
    fn: C.tankVolumeCylindrical,
  },
  {
    id: "pumping_rate", category: "production", name: "Pumping Rate",
    description: "Rate = Volume / Time. Use consistent units.",
    fields: [
      { key: "volume", label: "Volume", unit: "", placeholder: "e.g. 240" },
      { key: "time", label: "Time", unit: "", placeholder: "e.g. 8" },
    ],
    fn: C.pumpingRate,
  },
  {
    id: "chem_injection_rate", category: "production", name: "Chemical Injection Rate",
    description: "Injection rate = Dose × Production Rate. Use consistent units.",
    fields: [
      { key: "dose", label: "Desired Dose", unit: "", placeholder: "e.g. 25" },
      { key: "productionRate", label: "Production Rate", unit: "", placeholder: "e.g. 1200" },
    ],
    fn: C.chemicalInjectionRate,
  },

  // DIRECTIONAL — additions
  {
    id: "dls_basic", category: "directional", name: "Dogleg Severity (Basic)",
    description: "Simplified DLS estimate using inclination change only (ignores azimuth).",
    fields: [
      { key: "inc1", label: "Previous Inclination", unit: "°", placeholder: "e.g. 8.5" },
      { key: "inc2", label: "Current Inclination", unit: "°", placeholder: "e.g. 11.2" },
      { key: "courseLength", label: "Course Length", unit: "ft", placeholder: "e.g. 95" },
      { key: "bhaTool", label: "BHA tool (optional)", unit: "", type: "text", placeholder: "e.g. motor_6.75_1.0bend", optional: true, libraryKind: ["mud_motor", "rotary_steerable"] },
    ],
    fn: C.doglegSeverityBasic,
  },
  {
    id: "turn_rate", category: "directional", name: "Turn Rate",
    description: "Azimuth change per 100 ft of course length (wrap-corrected).",
    fields: [
      { key: "az1", label: "Starting Azimuth", unit: "°", placeholder: "e.g. 154" },
      { key: "az2", label: "Ending Azimuth", unit: "°", placeholder: "e.g. 162" },
      { key: "courseLength", label: "Course Length", unit: "ft", placeholder: "e.g. 95" },
      { key: "bhaTool", label: "BHA tool (optional)", unit: "", type: "text", placeholder: "e.g. motor_6.75_1.0bend", optional: true, libraryKind: ["mud_motor", "rotary_steerable"] },
    ],
    fn: C.turnRate,
  },
  {
    id: "departure_estimate", category: "directional", name: "Departure Estimate",
    description: "Simplified single-station estimate: Departure = Course Length × sin(inclination). For tied multi-station departure use the Minimum Curvature Survey or approved directional software.",
    fields: [
      { key: "courseLength", label: "Course Length", unit: "ft", placeholder: "e.g. 95" },
      { key: "inclination", label: "Inclination", unit: "°", placeholder: "e.g. 12" },
    ],
    fn: C.departureEstimate,
  },
  {
    id: "vertical_section", category: "directional", name: "Vertical Section",
    description: "Simplified field estimate: VS = Departure × cos(azimuth − target azimuth). Full survey calculations should use approved directional software.",
    fields: [
      { key: "departure", label: "Departure", unit: "ft", placeholder: "e.g. 220" },
      { key: "azimuth", label: "Current Azimuth", unit: "°", placeholder: "e.g. 154" },
      { key: "targetAzimuth", label: "Target Azimuth", unit: "°", placeholder: "e.g. 90" },
    ],
    fn: C.verticalSection,
  },
  {
    id: "min_curvature_survey", category: "directional", name: "Minimum Curvature Survey",
    description: "Multi-station survey table. Enter MD,INC,AZ rows; computes TVD/NS/EW/closure/VS.",
    fields: [
      { key: "surveyCsv", label: "Survey rows (MD,INC,AZ per line)", unit: "", type: "text", multiline: true, rows: 5, placeholder: "0,0,0\n500,2,45\n1000,5,50" },
      { key: "targetAzimuth", label: "Target Azimuth (for VS)", unit: "°", placeholder: "e.g. 90" },
    ],
    fn: C.minCurvSurvey,
  },
  {
    id: "closure_from_ne", category: "directional", name: "Closure Distance & Azimuth",
    description: "Closure distance and direction from explicit North/East displacements.",
    fields: [
      { key: "northing", label: "Northing", unit: "ft", placeholder: "e.g. 350" },
      { key: "easting", label: "Easting", unit: "ft", placeholder: "e.g. 220" },
    ],
    fn: C.closureFromNE,
  },
  {
    id: "vertical_section_ne", category: "directional", name: "Vertical Section (from N/E)",
    description: "Vertical section along a target azimuth from N/E displacements.",
    fields: [
      { key: "northing", label: "Northing", unit: "ft", placeholder: "e.g. 350" },
      { key: "easting", label: "Easting", unit: "ft", placeholder: "e.g. 220" },
      { key: "targetAzimuth", label: "Target Azimuth", unit: "°", placeholder: "e.g. 90" },
    ],
    fn: C.verticalSectionFromNE,
  },

  // STUCK PIPE & FISHING
  {
    id: "free_point_stretch", category: "stuck", name: "Free Point from Stretch",
    description: "Estimate free-pipe length from a measured stretch under pull. Planning aid only.",
    fields: [
      { key: "stretchInches", label: "Measured Stretch", unit: "in", placeholder: "e.g. 12" },
      { key: "pipeWeightLbFt", label: "Pipe Weight", unit: "lb/ft", placeholder: "e.g. 19.5" },
      { key: "pullForceLbf", label: "Pull Force Applied", unit: "lbf", placeholder: "e.g. 50000" },
    ],
    fn: C.freePointFromStretch,
  },
  {
    id: "pipe_light_heavy", category: "stuck", name: "Pipe Light / Pipe Heavy",
    description: "Compare hookload against buoyed string weight to flag pipe-light / pipe-heavy condition.",
    fields: [
      { key: "hookload", label: "Hookload", unit: "lbf", placeholder: "e.g. 180000" },
      { key: "stringAirWeight", label: "String Air Weight", unit: "lbf", placeholder: "e.g. 220000" },
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 10.0", keepUnit: true },
    ],
    fn: C.pipeLightHeavy,
  },
  {
    id: "safe_pull_margin", category: "stuck", name: "Safe Pull Margin",
    description: "Margin between current hookload and a published tensile rating × design factor. Use API and company limits.",
    fields: [
      { key: "tensileRatingLbf", label: "Pipe Tensile Rating", unit: "lbf", placeholder: "e.g. 400000" },
      { key: "designFactor", label: "Design Factor (≤ 1)", unit: "", placeholder: "e.g. 0.8" },
      { key: "currentHookload", label: "Current Hookload", unit: "lbf", placeholder: "e.g. 180000" },
    ],
    fn: C.maxSafePullMargin,
  },
  {
    id: "jar_impact", category: "stuck", name: "Jar Impact Worksheet",
    description: "Advanced / Requires Verification — structured worksheet only. Actual jar impact depends on jar design, fluid drag, string elasticity, friction, BHA configuration, hole angle, and manufacturer data.",
    fields: [
      { key: "jarType", label: "Jar Type", unit: "", type: "select", options: ["mechanical", "hydraulic"] },
      { key: "overpull", label: "Overpull at Surface", unit: "lbf", placeholder: "e.g. 60000" },
      { key: "stringWeight", label: "String Weight (buoyed)", unit: "lbf", placeholder: "e.g. 180000" },
      { key: "stretch", label: "Stretch Above Neutral", unit: "in", placeholder: "e.g. 18" },
      { key: "depth", label: "Depth", unit: "ft", placeholder: "e.g. 9500" },
      { key: "remarks", label: "Remarks", unit: "", type: "text", placeholder: "BHA, mud type, hole angle…", optional: true },
    ],
    fn: C.jarImpactWorksheet,
  },

  // TUBULARS
  {
    id: "burst_barlow", category: "tubulars", name: "Pipe Burst (Barlow)",
    description: "Internal burst pressure using Barlow's formula with API 0.875 wall tolerance. Confirm with manufacturer / API data.",
    fields: [
      { key: "yieldStrength", label: "Yield Strength (Yp)", unit: "psi", placeholder: "e.g. 80000" },
      { key: "wallThickness", label: "Wall Thickness (t)", unit: "in", placeholder: "e.g. 0.408" },
      { key: "od", label: "Outside Diameter (D)", unit: "in", placeholder: "e.g. 5" },
    ],
    fn: C.pipeBurstBarlow,
  },
  {
    id: "axial_buoyed", category: "tubulars", name: "Axial Load (with Buoyancy)",
    description: "Buoyed axial load along a uniform string section.",
    fields: [
      { key: "weightLbFt", label: "Weight per foot", unit: "lb/ft", placeholder: "e.g. 19.5" },
      { key: "length", label: "Length", unit: "ft", placeholder: "e.g. 9500" },
      { key: "mw", label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 10.0", keepUnit: true },
    ],
    fn: C.axialWithBuoyancy,
  },
  {
    id: "collapse_api", category: "tubulars", name: "Pipe Collapse Utilization",
    description: "Advanced / Requires Verification — utilization worksheet using a user-entered collapse rating. Does not compute API 5C3 four-regime collapse; axial-tension derating not included.",
    fields: [
      { key: "collapseRating", label: "Published Collapse Rating", unit: "psi", placeholder: "e.g. 5300" },
      { key: "externalPressure", label: "External Pressure", unit: "psi", placeholder: "e.g. 4200" },
      { key: "internalPressure", label: "Internal Pressure", unit: "psi", placeholder: "e.g. 0", optional: true },
      { key: "safetyFactor", label: "Safety Factor", unit: "", placeholder: "e.g. 1.0" },
    ],
    fn: C.pipeCollapseWorksheet,
  },
  {
    id: "tensile_capacity", category: "tubulars", name: "Pipe Tensile Capacity (Body Yield)",
    description: "Simplified — pipe body yield only (Pa = Yp × As). NOT a substitute for API joint-strength / connection-rating tables.",
    fields: [
      { key: "yieldStrength", label: "Yield Strength (Yp)", unit: "psi", placeholder: "e.g. 80000" },
      { key: "od", label: "Outside Diameter", unit: "in", placeholder: "e.g. 5" },
      { key: "id", label: "Inside Diameter", unit: "in", placeholder: "e.g. 4.276" },
      { key: "designFactor", label: "Design Factor (≤ 1)", unit: "", placeholder: "e.g. 0.9", optional: true },
    ],
    fn: C.pipeTensileCapacityWorksheet,
  },
  {
    id: "triaxial_summary", category: "tubulars", name: "Triaxial Stress Summary",
    description: "Advanced / Requires Verification — utilization worksheet using user-entered API/manufacturer ratings. Does not perform Von Mises triaxial or biaxial derating. Not a substitute for engineering review.",
    fields: [
      { key: "burstRating", label: "Burst Rating", unit: "psi", placeholder: "e.g. 7700" },
      { key: "collapseRating", label: "Collapse Rating", unit: "psi", placeholder: "e.g. 5300" },
      { key: "tensileRating", label: "Tensile Rating", unit: "lbf", placeholder: "e.g. 400000" },
      { key: "internalPressure", label: "Applied Internal Pressure", unit: "psi", placeholder: "e.g. 3500", optional: true },
      { key: "externalPressure", label: "Applied External Pressure", unit: "psi", placeholder: "e.g. 2200", optional: true },
      { key: "axialLoad", label: "Applied Axial Load (+/− tension)", unit: "lbf", placeholder: "e.g. 180000", optional: true },
      { key: "safetyFactor", label: "Safety Factor", unit: "", placeholder: "e.g. 1.25" },
    ],
    fn: C.triaxialUtilizationSummary,
  },

  // =========================================================================
  // Subsea Rig Formulas — Batch 1: Riser Volumes & Capacities (5 calculators).
  // These DO NOT live under Well Control. They are exposed only under the
  // `subsea-rig-formulas` category (route /categories/subsea-rig-formulas).
  // `stackMode: "subsea"` makes CalculatorRunner render the red subsea safety
  // banner and ensures the global Subsea filter shows them.
  // =========================================================================
  {
    id: "riser_capacity_per_foot", category: "subsea-rig-formulas", name: "Riser Capacity Per Foot",
    description: "Marine riser internal capacity per foot from riser ID. C = ID² / 1029.4 (bbl/ft).",
    stackMode: "subsea",
    fields: [
      { key: "riserId", label: "Riser ID", unit: "in", placeholder: "e.g. 19" },
    ],
    fn: C.riserCapacityPerFoot,
    to: [
      { id: "riser_total_internal_volume_from_capacity", fieldKey: "riserCapacityPerFt" },
    ],
  },
  {
    id: "riser_total_internal_volume_from_capacity", category: "subsea-rig-formulas", name: "Riser Total Internal Volume from Capacity",
    description: "Total riser internal volume from a known riser capacity per foot and riser length. V = C × L. Use this when capacity per foot is already known (e.g., from Riser Capacity Per Foot).",
    stackMode: "subsea",
    fields: [
      { key: "riserCapacityPerFt", label: "Riser Capacity", unit: "bbl/ft", placeholder: "e.g. 0.35069" },
      { key: "riserLength",        label: "Riser Length",   unit: "ft",     placeholder: "e.g. 5000" },
    ],
    fn: C.riserTotalInternalVolumeFromCapacity,
  },
  {
    id: "riser_steel_displacement_per_foot", category: "subsea-rig-formulas", name: "Riser Steel Displacement Per Foot",
    description: "Riser body / steel displacement per foot from riser OD and ID. D = (OD² − ID²) / 1029.4 (bbl/ft).",
    stackMode: "subsea",
    fields: [
      { key: "riserOd", label: "Riser OD", unit: "in", placeholder: "e.g. 21" },
      { key: "riserId", label: "Riser ID", unit: "in", placeholder: "e.g. 19" },
    ],
    fn: C.riserSteelDisplacementPerFoot,
  },
  {
    id: "riser_dp_annular_capacity_per_foot", category: "subsea-rig-formulas", name: "Riser × Drill Pipe Annular Capacity Per Foot",
    description: "Annular capacity per foot inside the marine riser around drill pipe. Ca = (Riser ID² − DP OD²) / 1029.4 (bbl/ft).",
    stackMode: "subsea",
    fields: [
      { key: "riserId", label: "Riser ID",      unit: "in", placeholder: "e.g. 19" },
      { key: "dpOd",    label: "Drill Pipe OD", unit: "in", placeholder: "e.g. 5" },
    ],
    fn: C.riserDpAnnularCapacityPerFoot,
    to: [
      { id: "riser_dp_annular_volume_total", fieldKey: "riserId" },
    ],
  },
  {
    id: "riser_dp_annular_volume_total", category: "subsea-rig-formulas", name: "Riser × Drill Pipe Annular Total Volume",
    description: "Total annular volume inside the marine riser around drill pipe. Va = ((Riser ID² − DP OD²) × L) / 1029.4 (bbl).",
    stackMode: "subsea",
    fields: [
      { key: "riserId",     label: "Riser ID",      unit: "in", placeholder: "e.g. 19" },
      { key: "dpOd",        label: "Drill Pipe OD", unit: "in", placeholder: "e.g. 5" },
      { key: "riserLength", label: "Riser Length",  unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.riserDpAnnularVolumeTotal,
  },

  // =========================================================================
  // Subsea Rig Formulas — Batch 2: Choke & Kill Line Volumes, Strokes, and
  // Circulation Time (10 calculators). Same category, same stackMode, same
  // subsea safety banner. Independent of any Well Control entry.
  // =========================================================================
  {
    id: "choke_line_capacity_per_foot", category: "subsea-rig-formulas", name: "Choke Line Capacity Per Foot",
    description: "Choke line internal capacity per foot from choke line ID. C = ID² / 1029.4 (bbl/ft).",
    stackMode: "subsea",
    fields: [
      { key: "chokeLineId", label: "Choke Line ID", unit: "in", placeholder: "e.g. 3" },
    ],
    fn: C.chokeLineCapacityPerFoot,
  },
  {
    id: "choke_line_total_volume", category: "subsea-rig-formulas", name: "Choke Line Total Volume",
    description: "Total choke line internal volume from choke line ID and length. V = ID² × L / 1029.4 (bbl).",
    stackMode: "subsea",
    fields: [
      { key: "chokeLineId",     label: "Choke Line ID",     unit: "in", placeholder: "e.g. 3" },
      { key: "chokeLineLength", label: "Choke Line Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.chokeLineTotalVolume,
    to: [
      { id: "total_choke_kill_line_volume", fieldKey: "chokeLineVolume" },
      { id: "choke_line_strokes",            fieldKey: "chokeLineVolume" },
      { id: "choke_line_circulation_time",   fieldKey: "chokeLineVolume" },
    ],
  },
  {
    id: "kill_line_capacity_per_foot", category: "subsea-rig-formulas", name: "Kill Line Capacity Per Foot",
    description: "Kill line internal capacity per foot from kill line ID. C = ID² / 1029.4 (bbl/ft).",
    stackMode: "subsea",
    fields: [
      { key: "killLineId", label: "Kill Line ID", unit: "in", placeholder: "e.g. 3" },
    ],
    fn: C.killLineCapacityPerFoot,
  },
  {
    id: "kill_line_total_volume", category: "subsea-rig-formulas", name: "Kill Line Total Volume",
    description: "Total kill line internal volume from kill line ID and length. V = ID² × L / 1029.4 (bbl).",
    stackMode: "subsea",
    fields: [
      { key: "killLineId",     label: "Kill Line ID",     unit: "in", placeholder: "e.g. 3" },
      { key: "killLineLength", label: "Kill Line Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.killLineTotalVolume,
    to: [
      { id: "total_choke_kill_line_volume", fieldKey: "killLineVolume" },
      { id: "kill_line_strokes",            fieldKey: "killLineVolume" },
      { id: "kill_line_circulation_time",   fieldKey: "killLineVolume" },
    ],
  },
  {
    id: "total_choke_kill_line_volume", category: "subsea-rig-formulas", name: "Total Choke + Kill Line Volume",
    description: "Combined choke line and kill line volume. V_total = V_choke + V_kill (bbl).",
    stackMode: "subsea",
    fields: [
      { key: "chokeLineVolume", label: "Choke Line Volume", unit: "bbl", placeholder: "e.g. 43.71" },
      { key: "killLineVolume",  label: "Kill Line Volume",  unit: "bbl", placeholder: "e.g. 43.71" },
    ],
    fn: C.totalChokeKillLineVolume,
    to: [
      { id: "combined_choke_kill_line_strokes", fieldKey: "totalLineVolume" },
    ],
  },
  {
    id: "choke_line_strokes", category: "subsea-rig-formulas", name: "Choke Line Strokes",
    description: "Pump strokes required to displace the choke line volume. Strokes = V_choke / pump_output.",
    stackMode: "subsea",
    fields: [
      { key: "chokeLineVolume", label: "Choke Line Volume", unit: "bbl",     placeholder: "e.g. 43.71" },
      { key: "pumpOutput",      label: "Pump Output",       unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk", placeholder: "e.g. 0.105" },
    ],
    fn: C.chokeLineStrokes,
  },
  {
    id: "kill_line_strokes", category: "subsea-rig-formulas", name: "Kill Line Strokes",
    description: "Pump strokes required to displace the kill line volume. Strokes = V_kill / pump_output.",
    stackMode: "subsea",
    fields: [
      { key: "killLineVolume", label: "Kill Line Volume", unit: "bbl",     placeholder: "e.g. 43.71" },
      { key: "pumpOutput",     label: "Pump Output",      unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk", placeholder: "e.g. 0.105" },
    ],
    fn: C.killLineStrokes,
  },
  {
    id: "choke_line_circulation_time", category: "subsea-rig-formulas", name: "Choke Line Circulation Time",
    description: "Time required to circulate / displace the choke line volume at the selected pump rate. t = V_choke / Q (min).",
    stackMode: "subsea",
    fields: [
      { key: "chokeLineVolume", label: "Choke Line Volume", unit: "bbl",     placeholder: "e.g. 43.71" },
      { key: "pumpRate",        label: "Pump Rate",         unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.chokeLineCirculationTime,
  },
  {
    id: "kill_line_circulation_time", category: "subsea-rig-formulas", name: "Kill Line Circulation Time",
    description: "Time required to circulate / displace the kill line volume at the selected pump rate. t = V_kill / Q (min).",
    stackMode: "subsea",
    fields: [
      { key: "killLineVolume", label: "Kill Line Volume", unit: "bbl",     placeholder: "e.g. 43.71" },
      { key: "pumpRate",       label: "Pump Rate",        unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.killLineCirculationTime,
  },
  {
    id: "combined_choke_kill_line_strokes", category: "subsea-rig-formulas", name: "Combined Choke + Kill Line Strokes",
    description: "Pump strokes required to displace the combined choke + kill line volume. Strokes = V_total / pump_output.",
    stackMode: "subsea",
    fields: [
      { key: "totalLineVolume", label: "Total Choke + Kill Line Volume", unit: "bbl",     placeholder: "e.g. 87.42" },
      { key: "pumpOutput",      label: "Pump Output",                    unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk", placeholder: "e.g. 0.105" },
    ],
    fn: C.combinedChokeKillLineStrokes,
  },

  // =========================================================================
  // Subsea Rig Formulas — Batch 3: Depth References + Riser Hydrostatics +
  // Riser Margin (15 calculators). Same category, same stackMode, same
  // subsea safety banner. Independent of any Well Control entry.
  // The existing Well Control "riser_margin_pressure" remains untouched.
  // =========================================================================
  {
    id: "rkb_to_mudline_depth", category: "subsea-rig-formulas", name: "RKB to Mudline Depth",
    description: "Depth from rotary kelly bushing (rig floor reference) to mudline. D = RKB_to_SeaLevel + WaterDepth.",
    stackMode: "subsea",
    fields: [
      { key: "rkbToSeaLevel", label: "RKB to Sea Level", unit: "ft", placeholder: "e.g. 100" },
      { key: "waterDepth",    label: "Water Depth",      unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.rkbToMudlineDepth,
    to: [
      { id: "water_depth_from_rkb_and_mudline", fieldKey: "rkbToMudline" },
      { id: "stack_height_allowance",           fieldKey: "rkbToMudline" },
      { id: "rkb_to_sea_level_from_mudline",    fieldKey: "rkbToMudline" },
    ],
  },
  {
    id: "water_depth_from_rkb_and_mudline", category: "subsea-rig-formulas", name: "Water Depth from RKB and Mudline",
    description: "Water depth when RKB-to-mudline and RKB-to-sea-level references are known. WD = RKB_to_Mudline − RKB_to_SeaLevel.",
    stackMode: "subsea",
    fields: [
      { key: "rkbToMudline",  label: "RKB to Mudline",   unit: "ft", placeholder: "e.g. 5100" },
      { key: "rkbToSeaLevel", label: "RKB to Sea Level", unit: "ft", placeholder: "e.g. 100" },
    ],
    fn: C.waterDepthFromRkbAndMudline,
  },
  {
    id: "subsea_wellhead_depth_from_rkb", category: "subsea-rig-formulas", name: "Subsea Wellhead Depth from RKB",
    description: "Subsea wellhead depth from the rig reference. D = RKB_to_SeaLevel + WaterDepth. Confirm datum references before use.",
    stackMode: "subsea",
    fields: [
      { key: "rkbToSeaLevel", label: "RKB to Sea Level", unit: "ft", placeholder: "e.g. 100" },
      { key: "waterDepth",    label: "Water Depth",      unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.subseaWellheadDepthFromRkb,
  },
  {
    id: "stack_depth_from_rkb", category: "subsea-rig-formulas", name: "Stack Depth from RKB",
    description: "Reference estimate of stack depth from rig reference using water depth and stack height allowance. Verify stack datum, stack height, connector datum, and rig-specific depth references.",
    stackMode: "subsea",
    fields: [
      { key: "rkbToSeaLevel",         label: "RKB to Sea Level",       unit: "ft", placeholder: "e.g. 100" },
      { key: "waterDepth",            label: "Water Depth",            unit: "ft", placeholder: "e.g. 5000" },
      { key: "stackHeightAllowance",  label: "Stack Height Allowance", unit: "ft", placeholder: "e.g. 50" },
    ],
    fn: C.stackDepthFromRkb,
    to: [
      { id: "stack_height_allowance", fieldKey: "stackDepthFromRkb" },
    ],
  },
  {
    id: "stack_height_allowance", category: "subsea-rig-formulas", name: "Stack Height Allowance",
    description: "Stack height allowance from stack depth and mudline depth reference. SH = StackDepth − RKB_to_Mudline.",
    stackMode: "subsea",
    fields: [
      { key: "stackDepthFromRkb", label: "Stack Depth from RKB", unit: "ft", placeholder: "e.g. 5150" },
      { key: "rkbToMudline",      label: "RKB to Mudline",       unit: "ft", placeholder: "e.g. 5100" },
    ],
    fn: C.stackHeightAllowance,
  },
  {
    id: "rkb_to_sea_level_from_mudline", category: "subsea-rig-formulas", name: "RKB to Sea Level from Mudline Reference",
    description: "RKB-to-sea-level distance when RKB-to-mudline and water depth are known. RKB_to_SeaLevel = RKB_to_Mudline − WaterDepth.",
    stackMode: "subsea",
    fields: [
      { key: "rkbToMudline", label: "RKB to Mudline", unit: "ft", placeholder: "e.g. 5100" },
      { key: "waterDepth",   label: "Water Depth",    unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.rkbToSeaLevelFromMudline,
  },
  {
    id: "riser_mud_hydrostatic_pressure", category: "subsea-rig-formulas", name: "Riser Mud Hydrostatic Pressure",
    description: "Hydrostatic pressure from mud in the marine riser. P = 0.052 × MW × Riser TVD.",
    stackMode: "subsea",
    fields: [
      { key: "mw",       label: "Mud Weight", unit: "ppg", rcePath: "mud.weightInPpg", placeholder: "e.g. 10.0" },
      { key: "riserTvd", label: "Riser TVD",  unit: "ft",  placeholder: "e.g. 5000" },
    ],
    fn: C.riserMudHydrostaticPressure,
    to: [
      { id: "riser_differential_hydrostatic_pressure", fieldKey: "mudHydrostatic" },
    ],
  },
  {
    id: "seawater_hydrostatic_pressure_rig", category: "subsea-rig-formulas", name: "Seawater Hydrostatic Pressure",
    description: "Seawater hydrostatic pressure from water depth and seawater gradient (default 0.445 psi/ft). Verify actual seawater density and gradient.",
    stackMode: "subsea",
    fields: [
      { key: "waterDepth",        label: "Water Depth",       unit: "ft",     placeholder: "e.g. 5000" },
      { key: "seawaterGradient",  label: "Seawater Gradient", unit: "psi/ft", placeholder: "e.g. 0.445", optional: true },
    ],
    fn: C.seawaterHydrostaticPressureRig,
    to: [
      { id: "riser_differential_hydrostatic_pressure", fieldKey: "seawaterHydrostatic" },
      { id: "seawater_gradient_from_pressure_and_depth", fieldKey: "seawaterHydrostatic" },
    ],
  },
  {
    id: "riser_differential_hydrostatic_pressure", category: "subsea-rig-formulas", name: "Riser Differential Hydrostatic Pressure",
    description: "Differential between riser mud hydrostatic and seawater hydrostatic. ΔP = P_mud − P_seawater.",
    stackMode: "subsea",
    fields: [
      { key: "mudHydrostatic",      label: "Riser Mud Hydrostatic Pressure", unit: "psi", placeholder: "e.g. 2600" },
      { key: "seawaterHydrostatic", label: "Seawater Hydrostatic Pressure",  unit: "psi", placeholder: "e.g. 2225" },
    ],
    fn: C.riserDifferentialHydrostaticPressure,
  },
  {
    id: "riser_margin_pressure_srf", category: "subsea-rig-formulas", name: "Riser Margin Pressure",
    description: "Pressure equivalent of a riser-margin mud weight over the water depth. P = 0.052 × RiserMargin × WaterDepth.",
    stackMode: "subsea",
    fields: [
      { key: "riserMarginPpg", label: "Riser Margin", unit: "ppg", placeholder: "e.g. 0.5" },
      { key: "waterDepth",     label: "Water Depth",  unit: "ft",  placeholder: "e.g. 5000" },
    ],
    fn: C.riserMarginPressureSrf,
  },
  {
    id: "required_riser_margin_mud_weight", category: "subsea-rig-formulas", name: "Required Riser Margin Mud Weight",
    description: "Mud-weight margin required to generate a desired pressure margin over water depth. M = ΔP / (0.052 × WaterDepth).",
    stackMode: "subsea",
    fields: [
      { key: "desiredPressure", label: "Desired Pressure Margin", unit: "psi", placeholder: "e.g. 130" },
      { key: "waterDepth",      label: "Water Depth",             unit: "ft",  placeholder: "e.g. 5000" },
    ],
    fn: C.requiredRiserMarginMudWeight,
  },
  {
    id: "mud_weight_equivalent_of_seawater_gradient", category: "subsea-rig-formulas", name: "Mud Weight Equivalent of Seawater Gradient",
    description: "Equivalent mud weight (ppg) of a seawater gradient. Equivalent Mud Weight = Seawater Gradient ÷ 0.052.",
    stackMode: "subsea",
    fields: [
      { key: "seawaterGradient", label: "Seawater Gradient", unit: "psi/ft", placeholder: "e.g. 0.445" },
    ],
    fn: C.mudWeightEquivalentOfSeawaterGradient,
  },
  {
    id: "mud_vs_seawater_gradient_difference", category: "subsea-rig-formulas", name: "Mud vs Seawater Gradient Difference",
    description: "Pressure-gradient difference between riser mud and seawater. ΔG = (0.052 × MW) − Gradient_seawater.",
    stackMode: "subsea",
    fields: [
      { key: "mw",                label: "Mud Weight",        unit: "ppg", rcePath: "mud.weightInPpg",    placeholder: "e.g. 10.0" },
      { key: "seawaterGradient",  label: "Seawater Gradient", unit: "psi/ft", placeholder: "e.g. 0.445" },
    ],
    fn: C.mudVsSeawaterGradientDifference,
    to: [
      { id: "riser_differential_from_gradient_difference", fieldKey: "gradientDifference" },
    ],
  },
  {
    id: "riser_differential_from_gradient_difference", category: "subsea-rig-formulas", name: "Riser Differential from Gradient Difference",
    description: "Riser differential pressure from gradient difference and water depth. ΔP = ΔG × WaterDepth.",
    stackMode: "subsea",
    fields: [
      { key: "gradientDifference", label: "Gradient Difference", unit: "psi/ft", placeholder: "e.g. 0.075" },
      { key: "waterDepth",         label: "Water Depth",         unit: "ft",     placeholder: "e.g. 5000" },
    ],
    fn: C.riserDifferentialFromGradientDifference,
  },
  {
    id: "seawater_gradient_from_pressure_and_depth", category: "subsea-rig-formulas", name: "Seawater Gradient from Pressure and Depth",
    description: "Seawater gradient from measured pressure and water depth. Gradient = P / WaterDepth.",
    stackMode: "subsea",
    fields: [
      { key: "seawaterHydrostatic", label: "Seawater Hydrostatic Pressure", unit: "psi", placeholder: "e.g. 2225" },
      { key: "waterDepth",          label: "Water Depth",                   unit: "ft",  placeholder: "e.g. 5000" },
    ],
    fn: C.seawaterGradientFromPressureAndDepth,
  },

  // =========================================================================
  // Subsea Rig Formulas — Final Beta Batch: Riser/Line Strokes, Circulation
  // Time, Boost Line, and Auxiliary Line Support (15 calculators).
  // All IDs end in `_srf` to guarantee no collision with existing calcs.
  // =========================================================================
  {
    id: "marine_riser_strokes_srf", category: "subsea-rig-formulas", name: "Marine Riser Strokes",
    description: "Pump strokes required to displace a selected marine riser volume. Strokes = V_riser / pump_output.",
    stackMode: "subsea",
    fields: [
      { key: "riserVolume", label: "Riser Volume", unit: "bbl",     placeholder: "e.g. 1753.45" },
      { key: "pumpOutput",  label: "Pump Output",  unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk", placeholder: "e.g. 0.105" },
    ],
    fn: C.marineRiserStrokesSrf,
  },
  {
    id: "marine_riser_circulation_time_srf", category: "subsea-rig-formulas", name: "Marine Riser Circulation Time",
    description: "Time required to circulate or displace a selected marine riser volume. t = V_riser / Q.",
    stackMode: "subsea",
    fields: [
      { key: "riserVolume", label: "Riser Volume", unit: "bbl",     placeholder: "e.g. 1753.45" },
      { key: "pumpRate",    label: "Pump Rate",    unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.marineRiserCirculationTimeSrf,
  },
  {
    id: "riser_annular_strokes_srf", category: "subsea-rig-formulas", name: "Riser Annular Strokes",
    description: "Pump strokes required to displace riser annular volume around drill pipe. Strokes = V_annular / pump_output.",
    stackMode: "subsea",
    fields: [
      { key: "riserAnnularVolume", label: "Riser Annular Volume", unit: "bbl",     placeholder: "e.g. 1632.02" },
      { key: "pumpOutput",         label: "Pump Output",          unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk", placeholder: "e.g. 0.105" },
    ],
    fn: C.riserAnnularStrokesSrf,
  },
  {
    id: "riser_annular_circulation_time_srf", category: "subsea-rig-formulas", name: "Riser Annular Circulation Time",
    description: "Time required to circulate riser annular volume around drill pipe. t = V_annular / Q.",
    stackMode: "subsea",
    fields: [
      { key: "riserAnnularVolume", label: "Riser Annular Volume", unit: "bbl",     placeholder: "e.g. 1632.02" },
      { key: "pumpRate",           label: "Pump Rate",            unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.riserAnnularCirculationTimeSrf,
  },
  {
    id: "choke_line_pump_rate_from_time_srf", category: "subsea-rig-formulas", name: "Choke Line Pump Rate from Time",
    description: "Required pump rate to displace the choke line volume in a selected time. Q = V_choke / t.",
    stackMode: "subsea",
    fields: [
      { key: "chokeLineVolume", label: "Choke Line Volume", unit: "bbl", placeholder: "e.g. 43.71" },
      { key: "time",            label: "Time",              unit: "min", placeholder: "e.g. 5.20" },
    ],
    fn: C.chokeLinePumpRateFromTimeSrf,
  },
  {
    id: "kill_line_pump_rate_from_time_srf", category: "subsea-rig-formulas", name: "Kill Line Pump Rate from Time",
    description: "Required pump rate to displace the kill line volume in a selected time. Q = V_kill / t.",
    stackMode: "subsea",
    fields: [
      { key: "killLineVolume", label: "Kill Line Volume", unit: "bbl", placeholder: "e.g. 43.71" },
      { key: "time",           label: "Time",             unit: "min", placeholder: "e.g. 5.20" },
    ],
    fn: C.killLinePumpRateFromTimeSrf,
  },
  {
    id: "combined_choke_kill_line_circulation_time_srf", category: "subsea-rig-formulas", name: "Combined Choke + Kill Line Circulation Time",
    description: "Time required to displace combined choke + kill line volume. t = V_total / Q.",
    stackMode: "subsea",
    fields: [
      { key: "totalLineVolume", label: "Total Choke + Kill Line Volume", unit: "bbl",     placeholder: "e.g. 87.42" },
      { key: "pumpRate",        label: "Pump Rate",                      unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.combinedChokeKillLineCirculationTimeSrf,
  },
  {
    id: "combined_choke_kill_line_pump_rate_from_time_srf", category: "subsea-rig-formulas", name: "Combined Choke + Kill Line Pump Rate from Time",
    description: "Required pump rate to displace combined choke + kill line volume in a selected time. Q = V_total / t.",
    stackMode: "subsea",
    fields: [
      { key: "totalLineVolume", label: "Total Choke + Kill Line Volume", unit: "bbl", placeholder: "e.g. 87.42" },
      { key: "time",            label: "Time",                           unit: "min", placeholder: "e.g. 10.41" },
    ],
    fn: C.combinedChokeKillLinePumpRateFromTimeSrf,
  },
  {
    id: "boost_line_capacity_per_foot_srf", category: "subsea-rig-formulas", name: "Boost Line Capacity Per Foot",
    description: "Boost line internal capacity per foot from boost line ID. C = ID² / 1029.4 (bbl/ft).",
    stackMode: "subsea",
    fields: [
      { key: "boostLineId", label: "Boost Line ID", unit: "in", placeholder: "e.g. 3" },
    ],
    fn: C.boostLineCapacityPerFootSrf,
  },
  {
    id: "boost_line_total_volume_srf", category: "subsea-rig-formulas", name: "Boost Line Total Volume",
    description: "Total boost line internal volume from boost line ID and length. V = ID² × L / 1029.4 (bbl).",
    stackMode: "subsea",
    fields: [
      { key: "boostLineId",     label: "Boost Line ID",     unit: "in", placeholder: "e.g. 3" },
      { key: "boostLineLength", label: "Boost Line Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.boostLineTotalVolumeSrf,
    to: [
      { id: "boost_line_strokes_srf",          fieldKey: "boostLineVolume" },
      { id: "boost_line_circulation_time_srf", fieldKey: "boostLineVolume" },
    ],
  },
  {
    id: "boost_line_strokes_srf", category: "subsea-rig-formulas", name: "Boost Line Strokes",
    description: "Pump strokes required to displace boost line volume. Strokes = V_boost / pump_output.",
    stackMode: "subsea",
    fields: [
      { key: "boostLineVolume", label: "Boost Line Volume", unit: "bbl",     placeholder: "e.g. 43.71" },
      { key: "pumpOutput",      label: "Pump Output",       unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk", placeholder: "e.g. 0.105" },
    ],
    fn: C.boostLineStrokesSrf,
  },
  {
    id: "boost_line_circulation_time_srf", category: "subsea-rig-formulas", name: "Boost Line Circulation Time",
    description: "Time required to displace boost line volume. t = V_boost / Q (min).",
    stackMode: "subsea",
    fields: [
      { key: "boostLineVolume", label: "Boost Line Volume", unit: "bbl",     placeholder: "e.g. 43.71" },
      { key: "pumpRate",        label: "Pump Rate",         unit: "bbl/min", placeholder: "e.g. 8.4" },
    ],
    fn: C.boostLineCirculationTimeSrf,
  },
  {
    id: "auxiliary_line_capacity_per_foot_srf", category: "subsea-rig-formulas", name: "Auxiliary Line Capacity Per Foot",
    description: "Auxiliary subsea line internal capacity per foot from line ID. C = ID² / 1029.4 (bbl/ft).",
    stackMode: "subsea",
    fields: [
      { key: "auxLineId", label: "Auxiliary Line ID", unit: "in", placeholder: "e.g. 2" },
    ],
    fn: C.auxiliaryLineCapacityPerFootSrf,
  },
  {
    id: "auxiliary_line_total_volume_srf", category: "subsea-rig-formulas", name: "Auxiliary Line Total Volume",
    description: "Total auxiliary subsea line internal volume from line ID and length. V = ID² × L / 1029.4 (bbl).",
    stackMode: "subsea",
    fields: [
      { key: "auxLineId",     label: "Auxiliary Line ID",     unit: "in", placeholder: "e.g. 2" },
      { key: "auxLineLength", label: "Auxiliary Line Length", unit: "ft", placeholder: "e.g. 5000" },
    ],
    fn: C.auxiliaryLineTotalVolumeSrf,
    to: [
      { id: "auxiliary_line_strokes_srf", fieldKey: "auxLineVolume" },
    ],
  },
  {
    id: "auxiliary_line_strokes_srf", category: "subsea-rig-formulas", name: "Auxiliary Line Strokes",
    description: "Pump strokes required to displace auxiliary subsea line volume. Strokes = V_aux / pump_output.",
    stackMode: "subsea",
    fields: [
      { key: "auxLineVolume", label: "Auxiliary Line Volume", unit: "bbl",     placeholder: "e.g. 19.43" },
      { key: "pumpOutput",    label: "Pump Output",           unit: "bbl/stk", rcePath: "rig.pumpOutputBblStk", placeholder: "e.g. 0.105" },
    ],
    fn: C.auxiliaryLineStrokesSrf,
  },
];

// ============================================================================
// Well Control: stack-mode tagging.
// Every existing well-control calculator is surface-stack. We tag them in a
// single pass here so we never have to touch each entry individually. New
// subsea calculators must explicitly set `stackMode: "subsea"` on the entry.
// ============================================================================
CALCULATORS.forEach((c) => {
  if (c.category === "wellcontrol" && !c.stackMode) c.stackMode = "surface";
});

export const STACK_MODES = ["surface", "subsea"];
export function getWellControlCalculators(stackMode) {
  return CALCULATORS.filter(
    (c) => c.category === "wellcontrol" && (c.stackMode || "surface") === stackMode,
  );
}

export function getCalculator(id) { return CALCULATORS.find((c) => c.id === id); }
export function getCalculatorsByCategory(catId) { return CALCULATORS.filter((c) => c.category === catId); }

// Dependency graph helpers — derived from `to` fields on each calculator.
export function getRelatedTo(calcId) {
  const c = getCalculator(calcId);
  if (!c || !c.to) return [];
  return c.to.map((t) => ({ ...t, target: getCalculator(t.id) })).filter((t) => t.target);
}
export function getRelatedFrom(calcId) {
  return CALCULATORS
    .filter((c) => Array.isArray(c.to) && c.to.some((t) => t.id === calcId))
    .map((c) => {
      const link = c.to.find((t) => t.id === calcId);
      return { id: c.id, name: c.name, link };
    });
}
